import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, HostListener, inject, DestroyRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ActionPlanComponent } from './action-plan/action-plan.component';
import { IssueManagementComponent } from './issue-management/issue-management.component';
import { ScheduleComponent } from './schedule/schedule.component';
import { ReviewerService } from '../core/services/reviewer.service';
import { RebarAuthService } from '../core/rebarauth/rebar.auth.service';
import { UserRoleService } from '../core/services/user-role.service';
import { MasterService } from '../core/services/master.service';
import { CountryOption, CityOption, ProposedYearOption, ProjectDetail, ReviewDocument } from '../planning/standard-review/standard.interfaces';
import { ReviewCycleComponent } from './review-cycle/review-cycle.component';
import { ReviewLocationCard, ReviewSummary, ReviewBasicDetailsForm } from './review-process.interfaces';
import { QUARTERS, QUARTER_MONTH_MAP, MARKETS, CLIENT_QUALITY_OPTIONS, Market, Quarter, MonthOption, ClientQuality } from '../planning/standard-review/standard.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';

const REVIEW_TYPE_CODE = 'STD';

@Component({
  selector: 'app-review-process',
  standalone: true,
  imports: [DatePipe, FormsModule, RouterLink, ActionPlanComponent, IssueManagementComponent, ScheduleComponent, ReviewCycleComponent],
  templateUrl: './review-process.component.html',
  styleUrl: './review-process.component.css',
})
export class ReviewProcessComponent implements OnInit, AfterViewInit {
  private destroyRef = inject(DestroyRef);
  private viewMode = false;
  private editMode = false;
  private actionMode = false;
  basicDetailsExpanded = true;
  scheduleExpanded = false;
  reviewCycleExpanded = false;
  issueManagementExpanded = false;
  actionPlanExpanded = false;
  isLoading = false;
  successToastMessage: string | null = null;
  draftSavedMessage = false;
  showErrorPopup = false;
  errorPopupMessages: string[] = [];
  submitAttempted = false;
  locationRequiredError = false;
  orgReviewDetails: any[] = [];

  summary: ReviewSummary = {
    reviewId: '',
    reviewName: '',
    clientName: '',
    market: '',
    marketUnit: '',
    locationToBeReviewed: '',
    country: '',
    proposedMonth: '',
    planFieldworkStartDate: '',
    planFieldworkEndDate: '',
    rcReviewer: '',
    rcSupervisor: '',
    rcManager: '',
    statusOfReview: '',
    singleMultipleLocation: '',
    isParentLocation: false,
    serviceLine: '',
    parentChildType: '',
    parentReviewId: '',
    targetPublicationDate: '',
    typeOfReview: '',
    typeOfProject: '',
    goLiveDate: '',
    preGoLiveFWStartDate: '',
    preGoLiveFWEndDate: '',
    postGoLiveFWStartDate: '',
    postGoLiveFWEndDate: '',
  };
  postGoLiveFWEndManuallyEdited = false;

  form: ReviewBasicDetailsForm = {
    reviewName: '',
    proposedYear: '',
    masterClientName: '',
    dtProjectName: '',
    contractNumber: '',
    additionalContractNumber: '',
    market: '',
    marketUnit: '',
    customerRelationshipName: '',
    planName: '',
    qadRecommendation: '',
    clientQuality: '',
    clientAccountLead: '',
    operationAccountLead: '',
    masterClientId: '',
    deliveryCountries: [],
    locationsToReview: [],
    comments: '',
    attachmentName: '',
    typeOfReview: '',
    typeOfProject: '',
  };

  locationCards: ReviewLocationCard[] = [];
  /** Per-card validation errors for the primary/secondary R&C pairs. */
  locationCardErrors: Array<{ manager?: string; supervisor?: string; reviewer?: string; duplicate?: string }> = [];
  /** Per-card errors shown when the Manager submits Scheduling with missing primary team members. */
  schedulePrimaryTeamErrors: Array<{ manager?: string; supervisor?: string; reviewer?: string }> = [];
  /** Tracks whether save was attempted on a per-card basis (for inline validation on card Save). */
  cardSaveAttempted: boolean[] = [];
  attachedFiles: File[] = [];
  prePopulatedDocuments: { fileName: string; filePath: string; documentId?: number | null; fileContent?: string }[] = [];

  readonly allowedFileExtensions = ['pdf', 'msg', 'xls', 'xlsx', 'doc', 'docx', 'txt', 'ppt', 'pptx', 'jpg', 'jpeg', 'png', 'zip'];
  readonly maxFileSizeBytes = 20 * 1024 * 1024;
  attachmentErrors: string[] = [];

  deliveryCountries: CountryOption[] = [];
  availableCities: CityOption[] = [];
  private citiesByCountryMasterID: Record<number, CityOption[]> = {};
  private _originalForm: ReviewBasicDetailsForm | null = null;
  private _proposedYearIdFallback = '';
  private _proposedYearIdValue = '';
  private _originalLocationCards: ReviewLocationCard[] = [];
  private _originalPrePopulatedDocuments: { fileName: string; filePath: string; documentId?: number | null; fileContent?: string }[] = [];
  /** ReviewIDs removed by the user in this session — filtered from server reload responses. */
  private _deletedReviewIds = new Set<number>();

  proposedYears: ProposedYearOption[] = [];
  projectDetails: ProjectDetail[] = [];
  masterClientNames: string[] = [];
  dtProjectNames: string[] = [];
  contractNumbers: string[] = [];
  clientSearchTerm = '';
  clientDropdownOpen = false;
  projectSearchTerm = '';
  projectDropdownOpen = false;
  countrySearchTerm = '';
  countryDropdownOpen = false;
  citySearchTerm = '';
  cityDropdownOpen = false;

  // Special Project — Type of Project dropdown (options from GetTypeofProject)
  typeOfProjectOptions: string[] = [];
  private typeOfProjectShortCodeMap = new Map<string, string>();
  typeOfProjectDropdownOpen = false;
  typeOfProjectSearchTerm = '';

  allUsers: { label: string; value: string; role: string }[] = [];
  supervisorDropdownKey: string | null = null;
  supervisorSearchTerm = '';
  reviewerDropdownKey: string | null = null;
  reviewerSearchTerm = '';
  managerDropdownKey: string | null = null;
  managerSearchTerm = '';

  // Go-Live card per-card edit mode
  goLiveCardEditMode: boolean[] = [];
  glCountryDropdownKey: number | null = null;
  glCountrySearchTerm = '';
  glCityDropdownKey: number | null = null;
  glCitySearchTerm = '';

  userName: string;
 
  readonly quarterOptions = QUARTERS;
  readonly markets: readonly Market[] = MARKETS;
  readonly clientQualityOptions: readonly ClientQuality[] = CLIENT_QUALITY_OPTIONS;
  reviewTypeCode = REVIEW_TYPE_CODE;
  reviewTypeId: number = 1; // 1 = Standard Review, 2 = Go-Live Review

  reviewId: number | null = null;
  financialYear: string | null = null;
  quarter: string | null = null;

  @ViewChild('reviewNameTextarea') reviewNameTextarea?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('clientSearchInput') clientSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('projectSearchInput') projectSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('countrySearchInput') countrySearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('citySearchInput') citySearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('locationField') locationField?: ElementRef<HTMLElement>;
  @ViewChild('typeOfProjectSearchInput') typeOfProjectSearchInput?: ElementRef<HTMLInputElement>;

  get isGoLive(): boolean {
    return this.reviewTypeId === 2;
  }

  get isOnDemand(): boolean {
    return this.reviewTypeId === 3;
  }

  get isSpecialProject(): boolean {
    return this.reviewTypeId === 4;
  }

  get usesGoLiveLayout(): boolean {
    return this.isGoLive || this.isOnDemand || this.isSpecialProject;
  }

  get isDraft(): boolean {
    if (this.isReadOnlyAccess) return false;
    return this.summary.statusOfReview === 'Draft';
  }

  get isStatusDraft(): boolean {
    return this.summary.statusOfReview === 'Draft';
  }

  get isPlanned(): boolean {
    return this.summary.statusOfReview === 'Planned';
  }

  get isDraftOrPlanned(): boolean {
    if (this.isReadOnlyAccess) return false;
    return this.summary.statusOfReview === 'Draft' || this.summary.statusOfReview === 'Planned';
  }

  /** True when the review is Planned and a child (non-parent) record — attachments are hidden then. */
  get isPlannedChild(): boolean {
    return this.isPlanned && !this.summary.isParentLocation;
  }

  get isFormDirty(): boolean {
    // Baseline not captured yet (page still loading) — nothing can be dirty.
    if (!this._originalForm) return false;
    return (
      JSON.stringify(this.form) !== JSON.stringify(this._originalForm) ||
      JSON.stringify(this.locationCards) !== JSON.stringify(this._originalLocationCards) ||
      this.attachedFiles.length > 0 ||
      JSON.stringify(this.prePopulatedDocuments) !== JSON.stringify(this._originalPrePopulatedDocuments)
    );
  }

  private resetDirtyBaseline(): void {
    this._originalForm = JSON.parse(JSON.stringify(this.form));
    this._originalLocationCards = JSON.parse(JSON.stringify(this.locationCards));
    this._originalPrePopulatedDocuments = JSON.parse(JSON.stringify(this.prePopulatedDocuments));
  }

  get filteredClientNames(): string[] {
    const term = this.clientSearchTerm.trim().toLowerCase();
    if (!term) return this.masterClientNames;
    return this.masterClientNames.filter(n => n.toLowerCase().includes(term));
  }

  get filteredProjectNames(): string[] {
    const term = this.projectSearchTerm.trim().toLowerCase();
    if (!term) return this.dtProjectNames;
    return this.dtProjectNames.filter(n => n.toLowerCase().includes(term));
  }

  get filteredCountries(): CountryOption[] {
    const term = this.countrySearchTerm.trim().toLowerCase();
    if (!term) return this.deliveryCountries;
    return this.deliveryCountries.filter(c => c.CountryName.toLowerCase().includes(term));
  }

  get filteredCities(): CityOption[] {
    const term = this.citySearchTerm.trim().toLowerCase();
    if (!term) return this.availableCities;
    return this.availableCities.filter(c => c.CityName.toLowerCase().includes(term));
  }


  get filteredTypeOfProjectOptions(): string[] {
    const term = this.typeOfProjectSearchTerm.trim().toLowerCase();
    if (!term) return this.typeOfProjectOptions;
    return this.typeOfProjectOptions.filter(n => n.toLowerCase().includes(term));
  }

  private loadTypeOfProjectOptions(): void {
    if (this.typeOfProjectOptions.length > 0) return;
    this.reviewerService.getTypeofProject()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const list: any[] = Array.isArray(response) ? response : (response?.value ?? response?.data ?? []);
          this.typeOfProjectShortCodeMap.clear();
          this.typeOfProjectOptions = list
            .map((item: any) => {
              const name = (item?.Name ?? '').toString().trim();
              const code = (item?.ShortCode ?? '').toString().trim();
              if (name && code) this.typeOfProjectShortCodeMap.set(name, code);
              return name;
            })
            .filter(Boolean);
        },
        error: (err) => {
          console.error('GetTypeofProject error:', err?.message);
        }
      });
  }

  toggleTypeOfProjectDropdown(): void {
    this.typeOfProjectDropdownOpen = !this.typeOfProjectDropdownOpen;
    if (this.typeOfProjectDropdownOpen) {
      this.typeOfProjectSearchTerm = '';
      setTimeout(() => this.typeOfProjectSearchInput?.nativeElement.focus(), 0);
    }
  }

  selectTypeOfProject(name: string): void {
    this.form.typeOfProject = name;
    this.typeOfProjectDropdownOpen = false;
    this.typeOfProjectSearchTerm = '';
    // The review name embeds the project type's short code (FY##-<CODE>-...).
    const code = this.typeOfProjectShortCodeMap.get(name);
    if (code) this.reviewTypeCode = code;
    this.updateReviewName();
  }

  get supervisors(): { label: string; value: string; role: string }[] {
    return this.allUsers;
  }

  get reviewers(): { label: string; value: string; role: string }[] {
    return this.allUsers;
  }

  get managers(): { label: string; value: string; role: string }[] {
    return this.allUsers;
  }

  // ── Supervisor dropdown ──────────────────────────────────────────────────

  get filteredSupervisors(): { label: string; value: string }[] {
    const term = this.supervisorSearchTerm.trim().toLowerCase();
    if (!term) return this.supervisors;
    return this.supervisors.filter(
      (s: { label: string; value: string }) => s.label.toLowerCase().includes(term) || s.value.toLowerCase().includes(term)
    );
  }

  // ── Reviewer dropdown ────────────────────────────────────────────────────

  get filteredReviewers(): { label: string; value: string }[] {
    const term = this.reviewerSearchTerm.trim().toLowerCase();
    if (!term) return this.reviewers;
    return this.reviewers.filter(
      (r: { label: string; value: string }) => r.label.toLowerCase().includes(term) || r.value.toLowerCase().includes(term)
    );
  }

  reviewerLabel(value: string): string {
    if (!value) return '';
    return this.reviewers.find((r: { label: string; value: string }) => r.value === value)?.label ?? value;
  }

  toggleReviewerDropdown(key: string): void {
    this.reviewerDropdownKey = this.reviewerDropdownKey === key ? null : key;
    this.reviewerSearchTerm = '';
  }

  selectReviewer(card: ReviewLocationCard, field: 'rcReviewer' | 'secondaryRcReviewer', value: string, cardIndex: number): void {
    card[field] = value;
    this.reviewerDropdownKey = null;
    this.reviewerSearchTerm = '';
    if (field === 'rcReviewer' && this.schedulePrimaryTeamErrors[cardIndex]) {
      delete this.schedulePrimaryTeamErrors[cardIndex].reviewer;
    }
    this.validateLocationPairs(cardIndex);
  }

  // ── Manager dropdown ─────────────────────────────────────────────────────

  get filteredManagers(): { label: string; value: string }[] {
    const term = this.managerSearchTerm.trim().toLowerCase();
    if (!term) return this.managers;
    return this.managers.filter(
      (m: { label: string; value: string }) => m.label.toLowerCase().includes(term) || m.value.toLowerCase().includes(term)
    );
  }

  managerLabel(value: string): string {
    if (!value) return '';
    return this.managers.find((m: { label: string; value: string }) => m.value === value)?.label ?? value;
  }

  toggleManagerDropdown(key: string): void {
    this.managerDropdownKey = this.managerDropdownKey === key ? null : key;
    this.managerSearchTerm = '';
  }

  selectManager(card: ReviewLocationCard, field: 'rcManager' | 'secondaryRcManager', value: string, cardIndex: number): void {
    card[field] = value;
    this.managerDropdownKey = null;
    this.managerSearchTerm = '';
    if (field === 'rcManager' && this.schedulePrimaryTeamErrors[cardIndex]) {
      delete this.schedulePrimaryTeamErrors[cardIndex].manager;
    }
    this.validateLocationPairs(cardIndex);
  }

  clearRoleField(
    card: ReviewLocationCard,
    field: 'rcManager' | 'rcSupervisor' | 'rcReviewer' | 'secondaryRcManager' | 'secondaryRcSupervisor' | 'secondaryRcReviewer',
    cardIndex: number,
    event: Event
  ): void {
    event.preventDefault();
    card[field] = '';
    this.validateLocationPairs(cardIndex);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (this.clientDropdownOpen && !target.closest('.client-select')) {
      this.clientDropdownOpen = false;
    }
    if (this.projectDropdownOpen && !target.closest('.project-select')) {
      this.projectDropdownOpen = false;
    }
    if (this.countryDropdownOpen && !target.closest('.country-select')) {
      this.countryDropdownOpen = false;
    }
    if (this.cityDropdownOpen && !target.closest('.city-select')) {
      this.cityDropdownOpen = false;
    }
    if (this.typeOfProjectDropdownOpen && !target.closest('.type-of-project-select')) {
      this.typeOfProjectDropdownOpen = false;
    }
    if (this.supervisorDropdownKey && !target.closest('.supervisor-select')) {
      this.supervisorDropdownKey = null;
    }
    if (this.reviewerDropdownKey && !target.closest('.reviewer-select')) {
      this.reviewerDropdownKey = null;
    }
    if (this.managerDropdownKey && !target.closest('.manager-select')) {
      this.managerDropdownKey = null;
    }
    if (this.glCountryDropdownKey !== null && !target.closest(`[class*="gl-rp-country-select-"]`)) {
      this.glCountryDropdownKey = null;
    }
    if (this.glCityDropdownKey !== null && !target.closest(`[class*="gl-rp-city-select-"]`)) {
      this.glCityDropdownKey = null;
    }
  }

  showCancelReviewModal = false;
  cancelReviewComment = '';
  cancelReviewFile: File | null = null;
  cancelReviewFileError = '';
  cancelReviewCommentError = false;
  isSendingCancelRequest = false;
  private _dynamicDataPending = false;

  get isCancelReviewFormDirty(): boolean {
    return this.cancelReviewComment.trim().length > 0 || this.cancelReviewFile !== null;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private reviewerService: ReviewerService,
    private rebarAuth: RebarAuthService,
    private userRoleService: UserRoleService,
    private masterService: MasterService,
  ) { }

  get isManagerRole(): boolean {
    return this.userRoleService.isManager;
  }

  get isAdminRole(): boolean {
    return this.userRoleService.isAdmin;
  }

  get isAssignedToReview(): boolean {
    const email = (this.userRoleService.email ?? '').toLowerCase();
    const enterpriseId = (this.userRoleService.enterpriseId ?? '').toLowerCase();
    // enterpriseId is authoritative; only fall back to email-derived if missing.
    const eid = enterpriseId || (email ? email.split('@')[0] : '');
    if (!eid) return false;
    const fields = [
      this.summary.rcReviewer, this.summary.rcSupervisor, this.summary.rcManager,
      ...(this.locationCards ?? []).flatMap(c => [
        c.rcManager, c.secondaryRcManager,
        c.rcSupervisor, c.secondaryRcSupervisor,
        c.rcReviewer, c.secondaryRcReviewer,
      ]),
    ];
    return fields.some(v => {
      if (!v) return false;
      return v.toLowerCase().split('@')[0] === eid;
    });
  }

  get isRCMember(): boolean {
    return this.userRoleService.isRCMember;
  }

  get isCancelled(): boolean {
    return this.summary.statusOfReview === 'cancelled';
  }

  get isIMPublished(): boolean {
    const status = (this.summary.statusOfReview ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
    return status === 'im - published' || status === 'im-published' || status === 'im published';
  }

  get isIMPublishedGoLive(): boolean {
    const status = (this.summary.statusOfReview ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
    return status === 'im - published - issue management';
  }

  get isIMPublishedIssuesClosed(): boolean {
    const status = (this.summary.statusOfReview ?? '').toLowerCase().replace(/[\s\-]+/g, ' ').trim();
    return status === 'im published issues closed';
  }

  get isRCTeamFieldsReadOnly(): boolean {
    if (!this.isIMPublishedIssuesClosed) return false;
    if (!Array.isArray(this.issueStatuses) || this.issueStatuses.length === 0) return false;
    if (!Array.isArray(this.actionPlanStatuses) || this.actionPlanStatuses.length === 0) return false;
    return (
      this.issueStatuses.every((s: any) => (s ?? '').toString().toLowerCase() === 'closed') &&
      this.actionPlanStatuses.every((s: any) => (s ?? '').toString().toLowerCase() === 'closed')
    );
  }

  get isViewMode(): boolean { return this.viewMode; }

  get isReadOnlyAccess(): boolean {
    if (this.isCancelled) return true;
    if (this.viewMode) return true;
    if (this.userRoleService.isAdmin) return false;
    // Non-admin: editable only when in action/edit mode AND assigned to this review.
    return !(this.editMode || this.actionMode) || !this.isAssignedToReview;
  }

  /** True only in action/edit mode when a non-admin user is NOT assigned to this review. */
  get isUnassignedActive(): boolean {
    if (!(this.editMode || this.actionMode)) return false;
    if (this.userRoleService.isAdmin) return false;
    return !this.isAssignedToReview;
  }

  get isReviewer(): boolean {
    return this.userRoleService.isReviewer;
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.adjustReviewNameHeight(), 0);
  }

  private adjustReviewNameHeight(): void {
    const el = this.reviewNameTextarea?.nativeElement;
    if (!el) return;
    if (el.value !== this.form.reviewName) {
      el.value = this.form.reviewName;
    }
    el.rows = 1;
    const style = getComputedStyle(el);
    const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.4;
    const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const contentHeight = el.scrollHeight - padding;
    el.rows = Math.max(1, Math.round(contentHeight / lineHeight));
  }

  ngOnInit(): void {
    this.userName = this.rebarAuth.getUserName();
    const state = this.router.lastSuccessfulNavigation?.extras?.state
      ?? (typeof history !== 'undefined' ? history.state : null);
    const qp = this.route.snapshot.queryParamMap;
    const entryMode = state?.['entryMode'] ?? qp.get('entryMode') ?? '';
    this.viewMode   = entryMode === 'view';
    this.editMode   = entryMode === 'edit';
    this.actionMode = entryMode === 'action';
    const rawQpReviewId = qp.get('reviewId');
    const stateReviewId: number | null = state?.['reviewId']
      ?? (rawQpReviewId ? Number(rawQpReviewId) : null);
    const stateDetails = state?.['reviewDetails'];
    this.orgReviewDetails = stateDetails;

    const submitMessage = state?.['submitSuccessMessage'];
    if (submitMessage) {
      this.successToastMessage = submitMessage;
      setTimeout(() => (this.successToastMessage = null), 5000);
      if (typeof history !== 'undefined') {
        const cleaned = { ...history.state };
        delete cleaned['submitSuccessMessage'];
        history.replaceState(cleaned, '');
      }
    }

    if (sessionStorage.getItem('expandIssueManagement') === 'true') {
      this.issueManagementExpanded = true;
      sessionStorage.removeItem('expandIssueManagement');
    }

    if (sessionStorage.getItem('expandActionPlan') === 'true') {
      this.actionPlanExpanded = true;
      sessionStorage.removeItem('expandActionPlan');
    }

    if (stateReviewId != null) {
      this.isLoading = true;
      this.reviewerService.getReviewByIDDynamic(stateReviewId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (details: any) => {
            this.applyReviewDetails(details, true);

            // Fall back to stateDetails so the form is populated before the dynamic fetch.
            if (!this.reviewId && stateDetails) {
              this.applyReviewDetails(stateDetails, true);
            }

            // Force view mode for closed statuses regardless of how the page was opened.
            const closedStatus = this.summary.statusOfReview;
            if ((closedStatus === 'IM-Published-Issues Closed' || closedStatus === 'IM - Published - Issues Closed')
                && (this.editMode || this.actionMode)) {
              this.viewMode   = true;
              this.editMode   = false;
              this.actionMode = false;
            }

            // Always fetch secondary locations using the originally-navigated reviewId.
            // this.reviewId may be set to the parent's ID by applyReviewDetails (when the API
            // returns parent-anchored data for a child record), so stateReviewId is the reliable anchor.
            if (stateReviewId) {
              this.fetchGoLiveDynamicData(stateReviewId, stateReviewId); // handles isLoading = false
            } else {
              this.isLoading = false;
            }
            if (this.viewMode || this.actionMode) {
              this.applyRelevantSectionOnly();
            } else if (this.editMode) {
              this.applyEditModeSections();
            }
          },
          error: () => {
            if (stateDetails) {
              this.applyReviewDetails(stateDetails, true);
            }
            if (stateReviewId) {
              this.fetchGoLiveDynamicData(stateReviewId, stateReviewId); // handles isLoading = false
            } else {
              this.isLoading = false;
            }
          }
        });
    } else if (stateDetails) {
      this.applyReviewDetails(stateDetails, true);
      if (this.reviewId) {
        this.fetchGoLiveDynamicData(this.reviewId);
      }
    }
  }

  /** In Edit mode, expand every section. Editing stays governed by isReadOnlyAccess (Cancelled = read-only). */
  private applyEditModeSections(): void {
    this.basicDetailsExpanded = true;
    this.scheduleExpanded = true;
    this.reviewCycleExpanded = true;
    this.issueManagementExpanded = true;
    this.actionPlanExpanded = true;
  }

  /** Expand only the section relevant to the review status (Cancelled expands all). Used by View and Action. */
  private applyRelevantSectionOnly(): void {
    this.basicDetailsExpanded = false;
    this.scheduleExpanded = false;
    this.reviewCycleExpanded = false;
    this.issueManagementExpanded = false;
    this.actionPlanExpanded = false;

    const status = (this.summary.statusOfReview || '').toLowerCase().replace(/\s+/g, ' ').trim();

    if (status === 'cancelled') {
      this.basicDetailsExpanded = true;
      this.scheduleExpanded = true;
      this.reviewCycleExpanded = true;
      this.issueManagementExpanded = true;
      this.actionPlanExpanded = true;
      return;
    }

    switch (this.getRelevantViewSection(status)) {
      case 'schedule': this.scheduleExpanded = true; break;
      case 'reviewCycle': this.reviewCycleExpanded = true; break;
      case 'issueManagement': this.issueManagementExpanded = true; break;
      default: this.basicDetailsExpanded = true; break;
    }
  }

  private getRelevantViewSection(status: string): 'basicDetails' | 'schedule' | 'reviewCycle' | 'issueManagement' {
    const s = (status || '').toLowerCase().trim();
    if (!s || s === 'draft' || s === 'planned') return 'basicDetails';
    if (s === 'scheduled') return 'schedule';

    // Status code before the first dash or space, e.g. "pl - kick off call held" -> "pl",
    // "interim call held" -> "interim". Handles hyphen, en-dash and em-dash.
    const code = s.split(/[-\u2013\u2014\s]/)[0].trim();
    if (code === 'im') return 'issueManagement';
    if (['pl', 'scp', 'fw', 'rptg', 'pre', 'post', 'interim'].includes(code)) return 'reviewCycle';

    return 'basicDetails';
  }


  private applyReviewDetails(details: any, fetchLookups: boolean): void {
    const anchorId = (typeof history !== 'undefined' && history.state)
      ? history.state['reviewId']
      : null;
    const d = Array.isArray(details)
      ? ((anchorId != null && details.find((item: any) => Number(item?.ReviewID) === Number(anchorId)))
        || details.find((item: any) => item?.IsParentLocation)
        || details[0])
      : details;
    if (!d) return;

    // Parse Fields array present in the dynamic API response format.
    const fields = this.parseLocationFields(d.Fields ?? []);

    // Parse the latest schedule entry's ScheduleFields (highest ScheduleID = most recent save).
    const scheduleArr: any[] = Array.isArray(d.Schedule) ? d.Schedule : (d.Schedule ? [d.Schedule] : []);
    const latestSchedule = scheduleArr.length > 0
      ? scheduleArr.reduce((a: any, b: any) => (Number(b.ScheduleID) > Number(a.ScheduleID) ? b : a))
      : null;
    const sf = latestSchedule ? this.parseLocationFields(latestSchedule.ScheduleFields ?? []) : {} as Record<string, string>;

    const detectedTypeId = Number(fields['ReviewTypeID'] || d.R_ReviewTypeID || d.ReviewTypeID || 0);
    if (detectedTypeId > 0) {
      this.reviewTypeId = detectedTypeId;
    }
    const typeCodeByReviewTypeId: Record<number, string> = { 1: 'STD', 2: 'GLRR', 3: 'ODR', 4: 'SPR' };
    const fallbackCode = typeCodeByReviewTypeId[this.reviewTypeId] || 'STD';
    if (this.reviewTypeId === 4 && d.ReviewName) {
      // SPR reviews embed the specific project type code (e.g. "DEFT") as the second
      // segment of ReviewName (format FY##-<CODE>-...). Recover it so updateReviewName()
      // doesn't overwrite the stored name with the generic "SPR".
      const segs = (d.ReviewName as string).split('-');
      const embedded = segs.length >= 2 && /^FY\d{2}$/.test(segs[0]) ? segs[1] : '';
      this.reviewTypeCode = embedded || d.ReviewTypeCode || fallbackCode;
    } else {
      this.reviewTypeCode = d.ReviewTypeCode || fallbackCode;
    }

    this.summary = {
      reviewId: String(d.ReviewID ?? ''),
      reviewName: d.ReviewName ?? '',
      clientName: fields['ClientName'] || d.R_ClientName || fields['MasterClientName'] || d.R_MasterClientName || '',
      market: fields['Market'] || d.R_Market || '',
      marketUnit: fields['MarketUnit'] || d.R_MarketUnit || '',
      locationToBeReviewed: fields['LocationToBeReviewed'] || d.R_LocationToBeReviewed || '',
      country: fields['DeliveryCountry'] || d.R_DeliveryCountry || '',
      proposedMonth: fields['ProposedMonth'] || d.R_ProposedMonth || '',
      planFieldworkStartDate: sf['PlanFieldworkStartDate'] || d.ScheduledFieldworkStartDate || d.FieldWorkStartDate || fields['FieldworkStartDate'] || '',
      planFieldworkEndDate: sf['PlanFieldworkEndDate'] || d.ScheduledFieldworkEndDate || d.FieldWorkEndDate || fields['FieldworkEndDate'] || '',
      rcReviewer: fields['RCReviewer'] || d.R_RCReviewer || '',
      rcSupervisor: fields['RCSupervisor'] || d.R_RCSupervisor || '',
      rcManager: fields['RCManager'] || d.R_RCManager || '',
      statusOfReview: d.CurrentStatus || d.ReviewStatus || '',
      singleMultipleLocation: fields['SingleMultipleLocation'] || d.R_SingleMultipleLocation || '',
      isParentLocation: fields['IsParentLocation'] ? fields['IsParentLocation'] === '1' : Number(d.IsParentLocation) === 1,
      serviceLine: sf['ServiceLine'] || d.S_ServiceLine || '',
      parentChildType: sf['ParentChildType'] || d.ParentChildType || '',
      parentReviewId: (d.ParentReviewIDTBD ?? d.ParentReviewID ?? d.R_ParentReviewID ?? (fields['ParentReviewID'] || null)) != null
        ? String(d.ParentReviewIDTBD ?? d.ParentReviewID ?? d.R_ParentReviewID ?? fields['ParentReviewID'])
        : '',
      targetPublicationDate: sf['TargetPublicationDate'] || d.TargetPublicationDate || '',
      typeOfReview: fields['TypeOfReview'] || d.TypeOfReview || d.R_TypeOfReview || '',
      typeOfProject: fields['TypeOfProject'] || d.TypeOfProject || d.R_TypeOfProject || '',
      goLiveDate: sf['GoLiveDate'] || fields['GoLiveDate'] || d.R_GoLiveDate || d.GoLiveDate || '',
      preGoLiveFWStartDate: sf['PreGoLiveFWStartDate'] || fields['PreGoLiveFWStartDate'] || d.R_PreGoLiveFWStartDate || d.PreGoLiveFWStartDate || '',
      preGoLiveFWEndDate: sf['PreGoLiveFWEndDate'] || fields['PreGoLiveFWEndDate'] || d.R_PreGoLiveFWEndDate || d.PreGoLiveFWEndDate || '',
      postGoLiveFWStartDate: sf['PostGoLiveFWStartDate'] || fields['PostGoLiveFWStartDate'] || d.R_PostGoLiveFWStartDate || d.PostGoLiveFWStartDate || '',
      postGoLiveFWEndDate: sf['PostGoLiveFWEndDate'] || fields['PostGoLiveFWEndDate'] || d.R_PostGoLiveFWEndDate || d.PostGoLiveFWEndDate || '',
    };
    // When the review is in Planned status, expand the Schedule section by default on page load.
    this.scheduleExpanded = (this.summary.statusOfReview || '').toLowerCase() === 'planned';

    if (this.isCancelled) {
      this.scheduleExpanded = false;
      this.reviewCycleExpanded = false;
      this.issueManagementExpanded = false;
      this.actionPlanExpanded = false;
    }

    if (!this.isIMPublished && !this.isIMPublishedIssuesClosed) {
      this.issueManagementExpanded = false;
      this.actionPlanExpanded = false;
    }

    this.reviewId = d.ReviewID ?? null;
    if (this.isIMPublishedIssuesClosed && this.reviewId) {
      this.getApiResponse();
    }
    this.financialYear = this.getFinancialYearLabel(String(d.ProposedYear ?? '')) || null;
    this.quarter = fields['Quarter'] || d.Quarter || null;

    const rawItems = Array.isArray(details) ? details : [details];

    // Exclude any locations the user deleted in this session so they don't reappear after reload.
    const reloadItems = this._deletedReviewIds.size > 0
      ? rawItems.filter((item: any) => item.ReviewID == null || !this._deletedReviewIds.has(item.ReviewID))
      : rawItems;

    // items: city-deduped, used only for form.locationsToReview (display list of city names).
    const seenCities = new Set<string>();
    const items = reloadItems.filter((item: any) => {
      const itemF = this.parseLocationFields(item.Fields ?? []);
      const city = itemF['LocationToBeReviewed'] || item.LocationCity || item.R_LocationToBeReviewed || null;
      if (!city || seenCities.has(city)) return false;
      seenCities.add(city);
      return true;
    });

    // cardItems: ReviewID-deduped, used for locationCards.
    // Keeps rows even when R_LocationToBeReviewed is empty so all card-level fields are populated.
    const seenCardIds = new Set<any>();
    const cardItems = reloadItems.filter((item: any) => {
      const key = item.ReviewID ?? item.R_LocationToBeReviewed ?? '';
      if (seenCardIds.has(key)) return false;
      seenCardIds.add(key);
      return true;
    });

    this.form = {
      reviewName: d.ReviewName ?? '',
      proposedYear: String(d.R_ProposedYearDisplayName || d.ProposedYear || fields['ProposedYear'] || ''),
      // ClientName/ProjectName are the canonical stored fields (also used by the listing grid);
      // the legacy names are only a fallback for older records.
      masterClientName: fields['ClientName'] || d.R_ClientName || fields['MasterClientName'] || d.R_MasterClientName || '',
      dtProjectName: fields['ProjectName'] || d['R_ProjectName'] || fields['D&TProjectName'] || d['R_D&TProjectName'] || '',
      contractNumber: fields['ContractNumber'] || d.R_ContractNumber || '',
      additionalContractNumber: fields['AdditionalContractNumber'] || d.R_AdditionalContractNumber || '',
      market: fields['Market'] || d.R_Market || '',
      marketUnit: fields['MarketUnit'] || d.R_MarketUnit || '',
      customerRelationshipName: fields['CustomerRelationshipName'] || d.R_CustomerRelationshipName || '',
      planName: fields['PlanName'] || d.R_PlanName || '',
      qadRecommendation: fields['QADRecommendation'] || d.R_QADRecommendation || '',
      clientQuality: fields['ClientQualityCQI'] || d.R_ClientQualityCQI || '',
      clientAccountLead: fields['ClientAccountLead'] || d.R_ClientAccountLead || '',
      operationAccountLead: fields['OperationAccountLead'] || d.R_OperationAccountLead || '',
      masterClientId: fields['MasterClientID'] || d.R_MasterClientID || '',
      deliveryCountries: Array.from(new Set(
        rawItems.map((item: any) => {
          const itemF = this.parseLocationFields(item.Fields ?? []);
          return (itemF['DeliveryCountry'] || item.R_DeliveryCountry || '') as string;
        }).filter((c: string) => !!c)
      )),
      locationsToReview: items.map((item: any) => {
        const itemF = this.parseLocationFields(item.Fields ?? []);
        return itemF['LocationToBeReviewed'] || item.LocationCity || item.R_LocationToBeReviewed || '';
      }).filter(Boolean),
      comments: fields['Comments'] || d.R_Comments || d.Comments || '',
      attachmentName: '',
      typeOfReview: fields['TypeOfReview'] || d.R_TypeOfReview || d.TypeOfReview || '',
      typeOfProject: fields['TypeOfProject'] || d.R_TypeOfProject || d.TypeOfProject || '',
    };
    this._proposedYearIdFallback = String(fields['ProposedYearId'] || fields['ProposedYear'] || '');
    this._proposedYearIdValue = String(fields['ProposedYearId'] || d.ProposedYearId || d.R_ProposedYearId || '');

    try {
      // d.Documents is a JSON string for standard reviews;
      // Go-Live reviews return d.documents as a direct array.
      const raw = d.Documents ?? d.documents ?? '[]';
      const docs = Array.isArray(raw) ? raw : JSON.parse(raw);
      this.prePopulatedDocuments = Array.isArray(docs)
        ? docs
          .map((doc: any) => ({
            fileName: doc.FileName ?? doc.fileName ?? '',
            filePath: doc.StorageFilePath ?? doc.storagePath ?? '',
            documentId: doc.DocumentID ?? doc.documentId ?? null,
            fileContent: doc.fileContent ?? undefined,
          }))
          .filter((doc: { fileName: string; filePath: string }) => !!doc.fileName)
        : [];
    } catch {
      this.prePopulatedDocuments = [];
    }

    // Build location cards from all locations in the response array.
    // Supports both the new Fields-array format and the legacy R_-prefixed flat format.
    let anyExplicitParentFlag = false;
    this.locationCards = cardItems.map((item: any) => {
      const itemF = this.parseLocationFields(item.Fields ?? []);
      if (itemF['IsParentLocation'] !== undefined || item.R_IsParentLocation !== undefined) {
        anyExplicitParentFlag = true;
      }
      return {
        location: itemF['LocationToBeReviewed'] || item.R_LocationToBeReviewed || item.LocationCity || '',
        deliveryCountry: itemF['DeliveryCountry'] || item.R_DeliveryCountry || '',
        quarter: itemF['Quarter'] || item.R_Quarter || '',
        proposedMonth: itemF['ProposedMonth'] || item.R_ProposedMonth || '',
        rcManager: itemF['RCManager'] || item.R_RCManager || '',
        rcSupervisor: itemF['RCSupervisor'] || item.R_RCSupervisor || '',
        rcReviewer: itemF['RCReviewer'] || item.R_RCReviewer || '',
        secondaryRcManager: itemF['SecondaryRCManager'] || itemF['secondaryRCManager'] || item.R_SecondaryRCManager || '',
        secondaryRcSupervisor: itemF['SecondaryRCSupervisor'] || itemF['secondaryRCSupervisor'] || item.R_SecondaryRCSupervisor || '',
        secondaryRcReviewer: itemF['SecondaryRCReviewer'] || itemF['secondaryRCReviewer'] || item.R_SecondaryRCReviewer || '',
        isParentLocation: itemF['IsParentLocation'] ? itemF['IsParentLocation'] === '1' : Number(item.R_IsParentLocation) === 1,
        reviewId: item.ReviewID ?? null,
        reviewTypeId: Number(itemF['ReviewTypeID'] || item.R_ReviewTypeID || 1),
      };
    });
    this.locationCardErrors = this.locationCards.map(() => ({}));
    this.goLiveCardEditMode = this.locationCards.map(() => false);
    this.cardSaveAttempted = this.locationCards.map(() => false);

    if (this.locationCards.length === 0 && this.isDraftOrPlanned && (this.usesGoLiveLayout || this.reviewTypeId === 1)) {
      this.addGoLiveLocationCard();
    }

    // Only default the first card to parent when the server never sent an explicit
    // IsParentLocation flag at all — an explicit "0" for a single loaded child must stay unchecked.
    if (this.locationCards.length > 0 && !this.locationCards.some(c => c.isParentLocation) && !anyExplicitParentFlag) {
      this.locationCards[0].isParentLocation = true;
    }
    // Derive isParentLocation from the anchor item (d) directly so a single-card
    // child review is not incorrectly promoted to parent by the guard above.
    this.summary.isParentLocation = fields['IsParentLocation']
      ? fields['IsParentLocation'] === '1'
      : Number(d.IsParentLocation) === 1;

    setTimeout(() => this.adjustReviewNameHeight(), 0);

    if (this.isSpecialProject) {
      this.loadTypeOfProjectOptions();
    }

    if (fetchLookups) {
      this.loadLookups();
    } else {
      // Reuse the already-loaded dropdown data so the page refreshes without a full reload.
      this.normalizeProposedYear();
      this.updateReviewName();
      this.rebuildProjectDropdowns();
      this.normalizeDeliveryCountries();
    }
  }

  private fetchGoLiveDynamicData(reviewId: number, anchorId?: number): void {
    this._dynamicDataPending = true;
    this.isLoading = true;
    this.reviewerService.getReviewByIDDynamic(reviewId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          // Documents — only apply if not already populated
          if (this.prePopulatedDocuments.length === 0) {
            const raw = response?.Documents ?? response?.documents ?? response?.Attachments ?? response?.attachments;
            if (raw) {
              const docs = Array.isArray(raw) ? raw : (() => { try { return JSON.parse(raw); } catch { return []; } })();
              if (Array.isArray(docs) && docs.length > 0) {
                this.prePopulatedDocuments = docs
                  .map((doc: any) => ({
                    fileName: doc.FileName ?? doc.fileName ?? '',
                    filePath: doc.StorageFilePath ?? doc.storagePath ?? doc.FilePath ?? '',
                    documentId: doc.DocumentID ?? doc.documentId ?? null,
                    fileContent: doc.fileContent ?? undefined,
                  }))
                  .filter((doc: { fileName: string; filePath: string }) => !!doc.fileName);
                this.resetDirtyBaseline();
              }
            }
          }

          // If the primary card has no location yet, populate it from Fields.
          // This covers the case where GetReviewDetailsByID didn't include R_LocationToBeReviewed.
          const primaryFields = this.parseLocationFields(response?.Fields ?? []);
          const primaryLocation = primaryFields['LocationToBeReviewed'] ?? '';
          if (primaryLocation && this.locationCards.length > 0 && !this.locationCards[0].location) {
            const pc = this.locationCards[0];
            pc.location             = primaryLocation;
            pc.deliveryCountry      = pc.deliveryCountry      || (primaryFields['DeliveryCountry']       ?? '');
            pc.quarter              = pc.quarter              || (primaryFields['Quarter']               ?? '');
            pc.proposedMonth        = pc.proposedMonth        || (primaryFields['ProposedMonth']         ?? '');
            pc.rcManager            = pc.rcManager            || (primaryFields['RCManager']             ?? '');
            pc.rcSupervisor         = pc.rcSupervisor         || (primaryFields['RCSupervisor']          ?? '');
            pc.rcReviewer           = pc.rcReviewer           || (primaryFields['RCReviewer']            ?? '');
            pc.secondaryRcManager   = pc.secondaryRcManager   || (primaryFields['SecondaryRCManager']    ?? primaryFields['secondaryRCManager']    ?? '');
            pc.secondaryRcSupervisor= pc.secondaryRcSupervisor|| (primaryFields['SecondaryRCSupervisor'] ?? primaryFields['secondaryRCSupervisor'] ?? '');
            pc.secondaryRcReviewer  = pc.secondaryRcReviewer  || (primaryFields['SecondaryRCReviewer']   ?? primaryFields['secondaryRCReviewer']   ?? '');
            this.resetDirtyBaseline();
          }

          // Secondary locations from Locations[] — add cards not already present.
          // Guard: only skip when loc.ReviewID is non-null AND matches an existing card's non-null reviewId.
          // Without this guard, null === null causes a false-positive duplicate match and drops the secondary card.
          const secondaryLocations: any[] = response?.Locations ?? [];
          let addedSecondary = false;
          for (const loc of secondaryLocations) {
            if (loc.ReviewID != null && this.locationCards.some(c => c.reviewId != null && c.reviewId === loc.ReviewID)) continue;
            if (loc.ReviewID != null && this._deletedReviewIds.has(loc.ReviewID)) continue;
            const fields = this.parseLocationFields(loc.LocationFields ?? []);
            this.locationCards.push({
              location: fields['LocationToBeReviewed'] ?? '',
              deliveryCountry: fields['DeliveryCountry'] ?? '',
              quarter: fields['Quarter'] ?? '',
              proposedMonth: fields['ProposedMonth'] ?? '',
              rcManager: fields['RCManager'] ?? '',
              rcSupervisor: fields['RCSupervisor'] ?? '',
              rcReviewer: fields['RCReviewer'] ?? '',
              secondaryRcManager: fields['SecondaryRCManager'] ?? fields['secondaryRCManager'] ?? '',
              secondaryRcSupervisor: fields['SecondaryRCSupervisor'] ?? fields['secondaryRCSupervisor'] ?? '',
              secondaryRcReviewer: fields['SecondaryRCReviewer'] ?? fields['secondaryRCReviewer'] ?? '',
              isParentLocation: false,
              reviewId: loc.ReviewID ?? null,
              reviewTypeId: this.reviewTypeId,
            });
            this.locationCardErrors.push({});
            this.schedulePrimaryTeamErrors.push({});
            this.goLiveCardEditMode.push(false);
            this.cardSaveAttempted.push(false);
            addedSecondary = true;
          }
          if (addedSecondary) {
            this.resetDirtyBaseline();
          }

          // When the user navigated to a specific child location (anchorId differs from this.reviewId
          // because the API returned parent-anchored data), update the summary to show child-specific fields.
          if (anchorId != null && anchorId !== this.reviewId) {
            const childCard = this.locationCards.find(c => c.reviewId === anchorId);
            if (childCard) {
              if (childCard.location) this.summary.locationToBeReviewed = childCard.location;
              if (childCard.deliveryCountry) this.summary.country = childCard.deliveryCountry;
              if (childCard.rcManager) this.summary.rcManager = childCard.rcManager;
              if (childCard.rcSupervisor) this.summary.rcSupervisor = childCard.rcSupervisor;
              if (childCard.rcReviewer) this.summary.rcReviewer = childCard.rcReviewer;
              this.summary.isParentLocation = childCard.isParentLocation;
            }
          }

          this._dynamicDataPending = false;
          this.isLoading = false;
          this.resetDirtyBaseline();
        },
        error: () => {
          this._dynamicDataPending = false;
          this.isLoading = false;
          this.resetDirtyBaseline();
        }
      });
  }

  private parseLocationFields(fields: any[]): Record<string, string> {
    const result: Record<string, string> = {};
    for (const f of fields ?? []) {
      if (f.FieldName) result[f.FieldName] = f.FieldValue ?? '';
    }
    return result;
  }

  /** Fetch the dropdown source data used by the Basic Details form (initial page load). */
  private loadLookups(): void {
    this.isLoading = true;
    let pendingLookups = 4;
    const lookupDone = () => {
      if (--pendingLookups <= 0 && !this._dynamicDataPending) {
        this.isLoading = false;
        this.resetDirtyBaseline();
      }
    };

    this.reviewerService.getPurposedYear()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (response: ProposedYearOption[]) => {
        this.proposedYears = response || [];
        this.normalizeProposedYear();
        this.updateReviewName();
        lookupDone();
      },
      error: (err) => { console.error('ProposedYear error:', err?.message); lookupDone(); }
    });

    this.reviewerService.getProjectDetails().subscribe({
      next: (response: ProjectDetail[]) => {
        this.projectDetails = response || [];
        this.rebuildProjectDropdowns();
        lookupDone();
      },
      error: (err) => { console.error('ProjectDetails error:', err?.message); lookupDone(); }
    });

    this.reviewerService.getAllCountries().subscribe({
      next: (response: CountryOption[]) => {
        this.deliveryCountries = response || [];
        this.normalizeDeliveryCountries();
        // Preload cities for existing Go-Live card countries now that deliveryCountries is ready
        if (this.isGoLive) {
          const uniqueCountries = Array.from(new Set(this.locationCards.map(c => c.deliveryCountry).filter(Boolean)));
          uniqueCountries.forEach(cn => this.loadCitiesForGlCard(cn));
        }
        lookupDone();
      },
      error: (err) => {
        console.error('AllCountries error status:', err?.status, 'message:', err?.message, 'full:', err);
        lookupDone();
      }
    });

    this.reviewerService.getUsersInformation('Supervisor')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (response: any) => {
        this.allUsers = this.mapUsersToOptions(response);
        lookupDone();
      },
      error: (err) => { console.error('GetUsersInformation (Supervisor) error:', err?.message); lookupDone(); }
    });
  }

  /** Normalises the GetUsersInformation response into { label, value, role } options for the dropdowns. */
  private mapUsersToOptions(response: any): { label: string; value: string; role: string }[] {
    const list: any[] = Array.isArray(response)
      ? response
      : (response?.value ?? response?.Users ?? response?.data ?? []);
    return list
      .map((u: any) => {
        const enterpriseId = (u?.EID ?? u?.EnterpriseId ?? u?.EnterpriseID ?? '').toString().trim();
        const email = (u?.InternetMail ?? u?.Email ?? u?.EmailId ?? u?.Mail ?? '').toString().trim();
        const value = enterpriseId || email;
        const label = enterpriseId || (u?.DisplayName ?? u?.UserName ?? u?.Name ?? u?.FullName ?? email).toString().trim();
        const role = (u?.Role_Name ?? u?.RoleName ?? '').toString().trim();
        return { label: (label || value).toString().trim(), value: (value || label).toString().trim(), role };
      })
      .filter(o => !!o.value);
  }

  /** Toggles the searchable supervisor dropdown identified by the given key. */
  toggleSupervisorDropdown(key: string): void {
    this.supervisorDropdownKey = this.supervisorDropdownKey === key ? null : key;
    this.supervisorSearchTerm = '';
  }

  supervisorLabel(value: string): string {
    if (!value) return '';
    return this.supervisors.find((s: { label: string; value: string }) => s.value === value)?.label ?? value;
  }

  /** Selects a supervisor for the given card field. */
  selectSupervisor(
    card: ReviewLocationCard,
    field: 'rcSupervisor' | 'secondaryRcSupervisor',
    value: string,
    cardIndex: number
  ): void {
    card[field] = value;
    this.supervisorDropdownKey = null;
    this.supervisorSearchTerm = '';
    if (field === 'rcSupervisor' && this.schedulePrimaryTeamErrors[cardIndex]) {
      delete this.schedulePrimaryTeamErrors[cardIndex].supervisor;
    }
    this.validateLocationPairs(cardIndex);
  }

  /** Validates that a location's primary and secondary R&C people are not the same. */
  validateLocationPairs(cardIndex: number): void {
    const card = this.locationCards[cardIndex];
    if (!card) return;
    const errors: { manager?: string; supervisor?: string; reviewer?: string; duplicate?: string } = {
      duplicate: this.locationCardErrors[cardIndex]?.duplicate,
    };
    if (card.rcManager && card.secondaryRcManager && card.rcManager === card.secondaryRcManager) {
      errors.manager = 'R&C Manager and Secondary R&C Manager cannot be the same';
    }
    if (card.rcSupervisor && card.secondaryRcSupervisor && card.rcSupervisor === card.secondaryRcSupervisor) {
      errors.supervisor = 'R&C Supervisor and Secondary R&C Supervisor cannot be the same';
    }
    if (card.rcReviewer && card.secondaryRcReviewer && card.rcReviewer === card.secondaryRcReviewer) {
      errors.reviewer = 'R&C Reviewer and Secondary R&C Reviewer cannot be the same';
    }
    this.locationCardErrors[cardIndex] = errors;
  }

  private static readonly DUPLICATE_LOCATION_MESSAGE = 'Cannot submit a review with duplicate locations.';

  /** Flags every card sharing the same Delivery Country + Location to be Reviewed with another card. */
  private refreshDuplicateLocationErrors(): boolean {
    const isSet = (c: ReviewLocationCard) => !!(c.deliveryCountry?.trim() && c.location?.trim());
    const keyOf = (c: ReviewLocationCard) =>
      `${c.deliveryCountry.trim().toLowerCase()}|${c.location.trim().toLowerCase()}`;
    const counts = new Map<string, number>();
    this.locationCards.forEach(c => {
      if (isSet(c)) counts.set(keyOf(c), (counts.get(keyOf(c)) ?? 0) + 1);
    });
    let hasDuplicate = false;
    this.locationCards.forEach((c, i) => {
      const isDup = isSet(c) && (counts.get(keyOf(c)) ?? 0) > 1;
      hasDuplicate = hasDuplicate || isDup;
      this.locationCardErrors[i] = {
        ...this.locationCardErrors[i],
        duplicate: isDup ? ReviewProcessComponent.DUPLICATE_LOCATION_MESSAGE : undefined,
      };
    });
    return hasDuplicate;
  }

  /** True when any location card has a primary/secondary pair error. */
  private hasLocationPairErrors(): boolean {
    return this.locationCardErrors.some(e => e && (e.manager || e.supervisor || e.reviewer));
  }

  // Generated by GitHub Copilot - Oct-22-2026
  /**
   * Resolve a ProposedYear value (e.g. "2025") to its ProposedYearId, which is what the
   * review record stores. Returns '' when the year isn't in the loaded options.
   */
  private getProposedYearId(year: string): string {
    const match = this.proposedYears.find(y => y.ProposedYear.toString() === year);
    return match ? match.ProposedYearId.toString() : '';
  }

  /** Normalise the stored proposed-year value to match the option format (ProposedYear as string). */
  private normalizeProposedYear(): void {
    // ProposedYearId is the authoritative stored value (saved on edit and read by the listing grid);
    // the legacy ProposedYear text can be stale, so the ID wins whenever it resolves.
    const idMatch = this._proposedYearIdValue
      ? this.proposedYears.find(y => y.ProposedYearId.toString() === this._proposedYearIdValue)
      : undefined;
    const valueToMatch = this.form.proposedYear || this._proposedYearIdFallback;
    const numericYear = (valueToMatch.match(/\d{4}/) || [])[0] || '';
    const yearMatch = idMatch ?? this.proposedYears.find(y =>
      y.ProposedYear.toString() === valueToMatch ||
      y.DisplayName === valueToMatch ||
      y.ProposedYearId.toString() === valueToMatch ||
      (numericYear !== '' && y.ProposedYear.toString() === numericYear)
    );
    if (yearMatch) {
      this.form.proposedYear = yearMatch.ProposedYear.toString();
      this._proposedYearIdFallback = '';
      this._proposedYearIdValue = '';
      if (this._originalForm) {
        this._originalForm.proposedYear = yearMatch.ProposedYear.toString();
      }
      this.financialYear = this.getFinancialYearLabel(yearMatch.ProposedYear.toString()) || this.financialYear;
    }
  }

  /** Rebuild the client/project/contract dependent dropdowns from the loaded project details. */
  private rebuildProjectDropdowns(): void {
    this.masterClientNames = Array.from(new Set(this.projectDetails.map(p => p.ClientName))).sort();
    if (this.form.masterClientName) {
      this.dtProjectNames = Array.from(new Set(
        this.projectDetails
          .filter(p => p.ClientName === this.form.masterClientName)
          .map(p => p.RepLetterProjectNm)
      ));
    }
    if (this.form.dtProjectName) {
      this.contractNumbers = this.projectDetails
        .filter(p => p.RepLetterProjectNm === this.form.dtProjectName)
        .map(p => p.ContractNumber);
    }
  }

  /** Map any stored country codes to names and load cities for every selected country. */
  private normalizeDeliveryCountries(): void {
    this.form.deliveryCountries = this.form.deliveryCountries.map(val => {
      const matched = this.deliveryCountries.find(c => c.CountryKey === val || c.CountryName === val);
      return matched ? matched.CountryName : val;
    });
    if (this._originalForm) {
      this._originalForm.deliveryCountries = [...this.form.deliveryCountries];
    }
    this.form.deliveryCountries.forEach(name => this.loadCitiesForCountry(name));
  }

  get isScheduled(): boolean {
    const s = this.summary.statusOfReview;
    return !!s && s !== 'Draft' && s !== 'Planned';
  }

  get isFwStarted(): boolean {
    const s = (this.summary.statusOfReview ?? '').toLowerCase().replace(/[\s\-]+/g, '');
    if (!s) return false;
    // Standard/ODR/SPR: FW Started and all statuses that follow
    if (s === 'fwstarted' || s === 'fwcompleted') return true;
    // Reporting-phase statuses (shared by standard and Go-Live)
    if (s.startsWith('rptg') || s === 'completed') return true;
    // IM-Published variants
    if (s.startsWith('im')) return true;
    // Go-Live: POST-Go-Live FW Started and all statuses that follow
    if (s.startsWith('postgolive')) return true;
    return false;
  }

  toggleBasicDetails() {
    this.basicDetailsExpanded = !this.basicDetailsExpanded;
    if (this.basicDetailsExpanded) {
      setTimeout(() => this.adjustReviewNameHeight(), 0);
    }
  }

  isScheduleHovered = false;
  scheduleTooltipX = 0;

  onScheduleMouseEnter(event: MouseEvent): void {
    this.isScheduleHovered = true;
    this.scheduleTooltipX = event.offsetX + 12;
  }

  toggleSchedule() {
    if (this.isStatusDraft) return;
    this.scheduleExpanded = !this.scheduleExpanded;
  }

  /**
   * After a successful Schedule submit: update the status, store the just-submitted values in
   * summary (so reopening the section shows them read-only without a page reload), and collapse it.
   */
  onScheduled(event: { status: string; data: any }) {
    const d = event.data ?? {};
    this.summary.statusOfReview = event.status;
    this.summary.serviceLine = d.ServiceLine ?? this.summary.serviceLine;
    this.summary.parentChildType = d.ParentChildType ?? this.summary.parentChildType;
    this.summary.parentReviewId = d.ParentReviewID != null ? String(d.ParentReviewID) : '';
    this.summary.planFieldworkStartDate = d.PlanFieldworkStartDate ?? this.summary.planFieldworkStartDate;
    this.summary.planFieldworkEndDate = d.PlanFieldworkEndDate ?? this.summary.planFieldworkEndDate;
    this.summary.targetPublicationDate = d.TargetPublicationDate ?? this.summary.targetPublicationDate;
    // Go-Live schedule fields — update summary so reopening the section loads saved values.
    if (d.GoLiveDate != null) this.summary.goLiveDate = d.GoLiveDate;
    if (d.PreGoLiveFWStartDate != null) this.summary.preGoLiveFWStartDate = d.PreGoLiveFWStartDate;
    if (d.PreGoLiveFWEndDate != null) this.summary.preGoLiveFWEndDate = d.PreGoLiveFWEndDate;
    if (d.PostGoLiveFWStartDate != null) this.summary.postGoLiveFWStartDate = d.PostGoLiveFWStartDate;
    if (d.PostGoLiveFWEndDate != null) this.summary.postGoLiveFWEndDate = d.PostGoLiveFWEndDate;
    this.postGoLiveFWEndManuallyEdited = this.postGoLiveFWEndManuallyEdited || !!d._postGoLiveFWEndManuallyEdited;
    this.scheduleExpanded = false;
    this.reviewCycleExpanded = true;
    this.successToastMessage = 'Schedule submitted successfully. Review is now Scheduled.';
    setTimeout(() => (this.successToastMessage = null), 5000);
  }

  /**
   * Called when the Schedule component detects missing primary team members during submit.
   * Sets per-card error messages and scrolls to the first missing field.
   */
  onMissingTeamMembers(missing: { cardIndex: number; role: string }[]): void {
    this.schedulePrimaryTeamErrors = this.locationCards.map(() => ({}));
    const roleLabel: Record<string, string> = {
      manager: 'R&C Manager',
      supervisor: 'R&C Supervisor',
      reviewer: 'R&C Reviewer',
    };
    for (const { cardIndex, role } of missing) {
      if (!this.schedulePrimaryTeamErrors[cardIndex]) {
        this.schedulePrimaryTeamErrors[cardIndex] = {};
      }
      (this.schedulePrimaryTeamErrors[cardIndex] as Record<string, string>)[role] =
        `${roleLabel[role]} is required.`;
    }
    const first = missing[0];
    setTimeout(() => {
      document
        .getElementById(`primary-${first.role}-field-${first.cardIndex}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  onScrollToLocations(): void {
    const wasCollapsed = !this.basicDetailsExpanded;
    if (wasCollapsed) {
      this.basicDetailsExpanded = true;
      setTimeout(() => this.adjustReviewNameHeight(), 0);
    }
    setTimeout(() => {
      document
        .querySelector('.gl-location-section-header')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, wasCollapsed ? 80 : 0);
  }

  toggleReviewCycle() {
    if (this.isStatusDraft || this.isPlanned) return;
    this.reviewCycleExpanded = !this.reviewCycleExpanded;
  }

  toggleIssueManagement() {
    if (this.isGoLive ? (!this.isIMPublishedGoLive && !this.isIMPublishedIssuesClosed) : (!this.isIMPublished && !this.isIMPublishedIssuesClosed)) return;
    this.issueManagementExpanded = !this.issueManagementExpanded;
  }

  toggleActionPlan() {
    if (this.isGoLive ? (!this.isIMPublishedGoLive && !this.isIMPublishedIssuesClosed) : (!this.isIMPublished && !this.isIMPublishedIssuesClosed)) return;
    this.actionPlanExpanded = !this.actionPlanExpanded;
  }

  setParentLocation(index: number) {
    this.locationCards.forEach((card, i) => (card.isParentLocation = i === index));
    // The review name is the parent location's name, so rebuild it when the parent changes.
    this.updateReviewName();
  }

  deleteLocationCard(index: number): void {
    if (this.locationCards.length === 1) {
      this.errorPopupMessages = ['At least one location is required. The last location card cannot be deleted.'];
      this.showErrorPopup = true;
      return;
    }
    const card = this.locationCards[index];
    if (card.reviewId != null) {
      this._deletedReviewIds.add(card.reviewId);
    }
    const wasParent = card.isParentLocation;
    this.locationCards = this.locationCards.filter((_, i) => i !== index);
    this.locationCardErrors = this.locationCardErrors.filter((_, i) => i !== index);
    this.schedulePrimaryTeamErrors = this.schedulePrimaryTeamErrors.filter((_, i) => i !== index);
    this.goLiveCardEditMode = this.goLiveCardEditMode.filter((_, i) => i !== index);
    this.cardSaveAttempted = this.cardSaveAttempted.filter((_, i) => i !== index);
    this.refreshDuplicateLocationErrors();
    this.form.locationsToReview = this.form.locationsToReview.filter(l => l !== card.location);
    if (wasParent && this.locationCards.length > 0 && !this.locationCards.some(c => c.isParentLocation)) {
      this.locationCards[0].isParentLocation = true;
    }
    this.updateReviewName();
  }

  addGoLiveLocationCard(): void {
    const isFirst = this.locationCards.length === 0;
    this.locationCards.push({
      location: '',
      deliveryCountry: '',
      quarter: '',
      proposedMonth: '',
      rcManager: '',
      rcSupervisor: '',
      rcReviewer: '',
      secondaryRcManager: '',
      secondaryRcSupervisor: '',
      secondaryRcReviewer: '',
      isParentLocation: isFirst,
      reviewId: null,
      reviewTypeId: this.reviewTypeId,
    });
    this.locationCardErrors.push({});
    this.schedulePrimaryTeamErrors.push({});
    this.goLiveCardEditMode.push(true);
    this.cardSaveAttempted.push(false);
  }

  deleteGoLiveLocationCard(index: number): void {
    if (this.locationCards.length === 1) {
      this.errorPopupMessages = ['At least one location is required. The last location card cannot be deleted.'];
      this.showErrorPopup = true;
      return;
    }
    const deletedId = this.locationCards[index].reviewId;
    if (deletedId != null) {
      this._deletedReviewIds.add(deletedId);
    }
    this.locationCards = this.locationCards.filter((_, i) => i !== index);
    this.locationCardErrors = this.locationCardErrors.filter((_, i) => i !== index);
    this.schedulePrimaryTeamErrors = this.schedulePrimaryTeamErrors.filter((_, i) => i !== index);
    this.goLiveCardEditMode = this.goLiveCardEditMode.filter((_, i) => i !== index);
    this.cardSaveAttempted = this.cardSaveAttempted.filter((_, i) => i !== index);
    this.refreshDuplicateLocationErrors();
    if (this.locationCards.length > 0 && !this.locationCards.some(c => c.isParentLocation)) {
      this.locationCards[0].isParentLocation = true;
    }
    this.updateReviewName();
  }

  removePrePopulatedDoc(index: number): void {
    const doc = this.prePopulatedDocuments[index];
    const documentId = doc?.documentId;

    // usp_UpdateReviewDynamic only appends its @Documents TVP, so a UI-only removal
    // reappears on the next reload — the row has to go through the delete API first.
    if (this.reviewId == null || documentId == null) {
      console.error('removePrePopulatedDoc: missing ReviewID or DocumentID', this.reviewId, doc);
      this.errorPopupMessages = ['Unable to delete the document. Please try again.'];
      this.showErrorPopup = true;
      return;
    }

    const eid = this.userRoleService.enterpriseId || (this.userRoleService.email ?? '').split('@')[0];

    // Same shape as removeFile() in review-cycle.component.ts: the row leaves the UI
    // only once the server confirms, so a failed delete never looks like a success.
    this.isLoading = true;
    this.reviewerService.deleteReviewDocument(this.reviewId, documentId, eid)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.isLoading = false))
      .subscribe({
        next: () => {
          this.prePopulatedDocuments = this.prePopulatedDocuments.filter((_, i) => i !== index);
          // Already gone server-side, so Cancel must not restore it and the form must not
          // read as dirty. Only the document baseline moves; unsaved edits to the form and
          // location cards keep their own dirty state.
          this._originalPrePopulatedDocuments = JSON.parse(JSON.stringify(this.prePopulatedDocuments));
        },
        error: (err: any) => {
          console.error('deleteReviewDocument error', err);
          this.errorPopupMessages = ['Unable to delete the document. Please try again.'];
          this.showErrorPopup = true;
        },
      });
  }

  downloadAttachedFile(file: File): void {
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  downloadPrePopulatedDoc(doc: { fileName: string; filePath: string; fileContent?: string }): void {
    if (!doc.fileContent) {
      // Blobs are stored under the review folder (StorageFilePath), so the lookup
      // must use the full path; the bare file name 404s in blob storage.
      const storagePath = doc.filePath || doc.fileName;
      this.isLoading = true;
      this.reviewerService.downloadFile(storagePath, this.reviewId)
        .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.isLoading = false))
        .subscribe({
          next: (blob: Blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = doc.fileName || doc.filePath.split('/').pop() || 'download';
            a.click();
            URL.revokeObjectURL(url);
          },
          error: (err: any) => console.error('Download error:', err?.status, err?.message),
        });
      return;
    }
    try {
      // fileContent is a base64-encoded JSON wrapper from the file-download service.
      // Unwrap it to get the actual file bytes and content type.
      const wrapper = JSON.parse(atob(doc.fileContent));
      const rawBase64 = wrapper.fileContent as string;
      const contentType: string = wrapper.contentType || 'application/octet-stream';
      if (!rawBase64) return;
      const binary = atob(rawBase64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // fileContent is raw base64 (not a JSON wrapper) — download as-is
      try {
        const binary = atob(doc.fileContent);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.fileName;
        a.click();
        URL.revokeObjectURL(url);
      } catch { }
    }
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    this.attachmentErrors = [];
    const validFiles: File[] = [];

    Array.from(input.files).forEach(file => {
      const ext = file.name.includes('.') ? (file.name.split('.').pop() ?? '').toLowerCase() : '';
      if (!this.allowedFileExtensions.includes(ext)) {
        this.attachmentErrors.push(`"${file.name}" is not a supported file type.`);
        return;
      }
      if (file.size === 0) {
        this.attachmentErrors.push(`"${file.name}" is empty (0 MB). Please upload a valid file with content.`);
        return;
      }
      if (file.size > this.maxFileSizeBytes) {
        this.attachmentErrors.push(`"${file.name}" exceeds the maximum file size of 20 MB.`);
        return;
      }
      validFiles.push(file);
    });

    const newFiles = validFiles.filter(
      f => !this.attachedFiles.some(existing => existing.name === f.name && existing.size === f.size)
    );
    this.attachedFiles = [...this.attachedFiles, ...newFiles];
    input.value = '';
  }

  removeAttachedFile(index: number): void {
    this.attachedFiles = this.attachedFiles.filter((_, i) => i !== index);
    this.attachmentErrors = [];
  }

  onCancel(): void {
    this.submitAttempted = false;
    this._deletedReviewIds.clear();
    if (this._originalForm) {
      this.form = JSON.parse(JSON.stringify(this._originalForm));
    }
    this.locationCards = JSON.parse(JSON.stringify(this._originalLocationCards));
    this.attachedFiles = [];
    this.prePopulatedDocuments = JSON.parse(JSON.stringify(this._originalPrePopulatedDocuments));
    if (this.isPlanned) {
      this.basicDetailsExpanded = false;
    }
    setTimeout(() => this.adjustReviewNameHeight(), 0);
  }

  // ── Go-Live card Edit/Save ────────────────────────────────────────────────

  private get isPreFieldwork(): boolean {
    if (this.isReadOnlyAccess || this.isCancelled) return false;
    const s = this.summary.statusOfReview;
    return s === 'Draft' || s === 'Planned' || s === 'PL - FW Confirmed';
  }

  isGoLiveCardEditing(i: number): boolean {
    if (this.isDraftOrPlanned) return this.goLiveCardEditMode[i] ?? false;
    if (this.isFwStarted) return false;
    if (this.isPreFieldwork) return true;
    return this.goLiveCardEditMode[i] ?? false;
  }

  isTeamFieldEditing(i: number): boolean {
    if (this.isRCTeamFieldsReadOnly) return false;
    if (this.isDraftOrPlanned) return this.goLiveCardEditMode[i] ?? false;
    if (this.isPreFieldwork) return true;
    return this.goLiveCardEditMode[i] ?? false;
  }

  private matchesCurrentUser(value: string): boolean {
    if (!value) return false;
    const email = (this.userRoleService.email ?? '').toLowerCase();
    if (!email) return false;
    const eid = email.split('@')[0];
    const val = value.toLowerCase();
    return val.includes('@') ? val === email : val === eid;
  }

  getUserRoleInCard(card: ReviewLocationCard): string {
    if (this.matchesCurrentUser(card.rcManager)) return 'rcManager';
    if (this.matchesCurrentUser(card.rcSupervisor)) return 'rcSupervisor';
    if (this.matchesCurrentUser(card.rcReviewer)) return 'rcReviewer';
    if (this.matchesCurrentUser(card.secondaryRcManager)) return 'secondaryRcManager';
    if (this.matchesCurrentUser(card.secondaryRcSupervisor)) return 'secondaryRcSupervisor';
    if (this.matchesCurrentUser(card.secondaryRcReviewer)) return 'secondaryRcReviewer';
    return 'none';
  }

  canEditRoleField(cardIndex: number, field: 'rcManager' | 'rcSupervisor' | 'rcReviewer' | 'secondaryRcManager' | 'secondaryRcSupervisor' | 'secondaryRcReviewer'): boolean {
    if (this.isAdminRole) return true;
    const card = this.locationCards[cardIndex];
    if (!card) return false;
    if (card.reviewId == null) return true;
    const userRole = this.getUserRoleInCard(card);
    const matrix: Record<string, Record<string, boolean>> = {
      rcManager:             { rcManager: true, rcSupervisor: true,  rcReviewer: true,  secondaryRcManager: true,  secondaryRcSupervisor: true,  secondaryRcReviewer: true  },
      rcSupervisor:          { rcManager: false, rcSupervisor: false, rcReviewer: true,  secondaryRcManager: false, secondaryRcSupervisor: true,  secondaryRcReviewer: true  },
      rcReviewer:            { rcManager: false, rcSupervisor: false, rcReviewer: false, secondaryRcManager: false, secondaryRcSupervisor: false, secondaryRcReviewer: true  },
      secondaryRcManager:    { rcManager: false, rcSupervisor: true,  rcReviewer: true,  secondaryRcManager: false, secondaryRcSupervisor: true,  secondaryRcReviewer: true  },
      secondaryRcSupervisor: { rcManager: false, rcSupervisor: false, rcReviewer: true,  secondaryRcManager: false, secondaryRcSupervisor: false, secondaryRcReviewer: true  },
      secondaryRcReviewer:   { rcManager: false, rcSupervisor: false, rcReviewer: false, secondaryRcManager: false, secondaryRcSupervisor: false, secondaryRcReviewer: false },
    };
    return matrix[userRole]?.[field] ?? false;
  }

  startEditGoLiveCard(i: number): void { this.goLiveCardEditMode[i] = true; }

  saveGoLiveCard(i: number): void {
    this.cardSaveAttempted[i] = true;
    const card = this.locationCards[i];
    if (!card.deliveryCountry || !card.location || !card.rcManager || !card.rcSupervisor || !card.rcReviewer) {
      setTimeout(() => {
        document.querySelector('.location-card .field-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return;
    }
    this.cardSaveAttempted[i] = false;
    this.goLiveCardEditMode[i] = false;
    if (this.glCountryDropdownKey === i) this.glCountryDropdownKey = null;
    if (this.glCityDropdownKey === i) this.glCityDropdownKey = null;
    if (this.managerDropdownKey?.endsWith('-' + i)) this.managerDropdownKey = null;
    if (this.supervisorDropdownKey?.endsWith('-' + i)) this.supervisorDropdownKey = null;
    if (this.reviewerDropdownKey?.endsWith('-' + i)) this.reviewerDropdownKey = null;
  }

  filteredGlCardCountries(): CountryOption[] {
    const term = this.glCountrySearchTerm.trim().toLowerCase();
    if (!term) return this.deliveryCountries;
    return this.deliveryCountries.filter(c => c.CountryName.toLowerCase().includes(term));
  }

  filteredGlCitiesForCard(card: ReviewLocationCard): CityOption[] {
    const country = this.deliveryCountries.find(c => c.CountryName === card.deliveryCountry);
    const cities = country ? (this.citiesByCountryMasterID[country.CountryMasterID] || []) : [];
    const term = this.glCitySearchTerm.trim().toLowerCase();
    return term ? cities.filter(c => c.CityName.toLowerCase().includes(term)) : cities;
  }

  toggleGlCardCountryDropdown(i: number): void {
    if (this.glCountryDropdownKey === i) {
      this.glCountryDropdownKey = null;
    } else {
      this.glCountryDropdownKey = i;
      this.glCityDropdownKey = null;
      this.glCountrySearchTerm = '';
    }
  }

  addGlCardCountry(card: ReviewLocationCard, countryName: string): void {
    card.deliveryCountry = countryName;
    card.location = '';
    this.glCountryDropdownKey = null;
    this.glCountrySearchTerm = '';
    this.loadCitiesForGlCard(countryName);
    this.refreshDuplicateLocationErrors();
  }

  toggleGlCityDropdown(i: number): void {
    if (this.glCityDropdownKey === i) {
      this.glCityDropdownKey = null;
    } else {
      this.glCityDropdownKey = i;
      this.glCountryDropdownKey = null;
      this.glCitySearchTerm = '';
    }
  }

  selectGlCity(card: ReviewLocationCard, cityName: string): void {
    card.location = cityName;
    this.glCitySearchTerm = '';
    this.glCityDropdownKey = null;
    this.refreshDuplicateLocationErrors();
    this.updateReviewName();
  }

  private loadCitiesForGlCard(countryName: string): void {
    if (!countryName) return;
    const country = this.deliveryCountries.find(c => c.CountryName === countryName);
    if (!country) return;
    if (this.citiesByCountryMasterID[country.CountryMasterID]) return;
    this.isLoading = true;
    this.reviewerService.getAllCities(country.CountryMasterID).subscribe({
      next: (response: any) => {
        this.citiesByCountryMasterID[country.CountryMasterID] = Array.isArray(response) ? response : [];
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Cities error for', countryName, err);
        this.isLoading = false;
      }
    });
  }

  private loadCitiesForCountry(countryName: string): void {
    if (!countryName) return;
    const country = this.deliveryCountries.find(c => c.CountryName === countryName);
    if (!country) return;
    if (this.citiesByCountryMasterID[country.CountryMasterID]) {
      this.rebuildAvailableCities();
      return;
    }
    this.isLoading = true;
    this.reviewerService.getAllCities(country.CountryMasterID).subscribe({
      next: (response: any) => {
        this.citiesByCountryMasterID[country.CountryMasterID] = Array.isArray(response) ? response : [];
        this.rebuildAvailableCities();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Cities error for', countryName, err);
        this.isLoading = false;
      }
    });
  }

  private rebuildAvailableCities(): void {
    const allCities: CityOption[] = [];
    const seen = new Set<number>();
    for (const countryName of this.form.deliveryCountries) {
      const country = this.deliveryCountries.find(c => c.CountryName === countryName);
      if (!country) continue;
      for (const city of (this.citiesByCountryMasterID[country.CountryMasterID] || [])) {
        if (!seen.has(city.CityID)) {
          seen.add(city.CityID);
          allCities.push(city);
        }
      }
    }
    this.availableCities = allCities;
  }

  private getCountryForCity(cityName: string): string {
    for (const countryName of this.form.deliveryCountries) {
      const country = this.deliveryCountries.find(c => c.CountryName === countryName);
      if (!country) continue;
      const found = (this.citiesByCountryMasterID[country.CountryMasterID] || [])
        .some(c => c.CityName === cityName);
      if (found) return countryName;
    }
    return this.form.deliveryCountries[0] ?? '';
  }

  toggleCountryDropdown(): void {
    this.countryDropdownOpen = !this.countryDropdownOpen;
    if (this.countryDropdownOpen) {
      this.countrySearchTerm = '';
      this.cityDropdownOpen = false;
      setTimeout(() => this.countrySearchInput?.nativeElement.focus(), 0);
    }
  }

  selectCountry(name: string): void {
    if (!this.form.deliveryCountries.includes(name)) {
      this.form.deliveryCountries = [...this.form.deliveryCountries, name];
      this.loadCitiesForCountry(name);
    }
    this.locationRequiredError = false;
    this.countryDropdownOpen = false;
    this.countrySearchTerm = '';
  }

  removeCountry(name: string): void {
    this.form.deliveryCountries = this.form.deliveryCountries.filter(c => c !== name);
    const country = this.deliveryCountries.find(c => c.CountryName === name);
    if (country) {
      const citiesForCountry = new Set(
        (this.citiesByCountryMasterID[country.CountryMasterID] || []).map(c => c.CityName)
      );
      this.form.locationsToReview = this.form.locationsToReview.filter(loc => !citiesForCountry.has(loc));
      this.buildLocationCards();
    }
    this.rebuildAvailableCities();
  }

  toggleCityDropdown(): void {
    this.cityDropdownOpen = !this.cityDropdownOpen;
    if (this.cityDropdownOpen) {
      this.citySearchTerm = '';
      this.countryDropdownOpen = false;
      setTimeout(() => this.citySearchInput?.nativeElement.focus(), 0);
    }
  }

  selectCity(name: string): void {
    if (!this.form.locationsToReview.includes(name)) {
      this.form.locationsToReview = [...this.form.locationsToReview, name];
      this.buildLocationCards();
    }
    this.cityDropdownOpen = false;
    this.citySearchTerm = '';
  }

  removeLocation(loc: string): void {
    if (!this.isDraft && this.form.locationsToReview.length === 1) {
      this.errorPopupMessages = ['At least one location is required. The last location cannot be deleted.'];
      this.showErrorPopup = true;
      return;
    }
    // Remember which country this city belongs to before removing it.
    const country = this.getCountryForCity(loc);
    this.form.locationsToReview = this.form.locationsToReview.filter(l => l !== loc);

    // If no remaining city belongs to that country, drop the country chip too.
    if (country) {
      const countryObj = this.deliveryCountries.find(c => c.CountryName === country);
      const citiesForCountry = new Set(
        (countryObj ? (this.citiesByCountryMasterID[countryObj.CountryMasterID] || []) : []).map(c => c.CityName)
      );
      const stillHasCity = this.form.locationsToReview.some(l => citiesForCountry.has(l));
      if (!stillHasCity) {
        this.form.deliveryCountries = this.form.deliveryCountries.filter(c => c !== country);
        this.rebuildAvailableCities();
      }
    }

    this.buildLocationCards();
  }

  toggleClientDropdown() {
    this.clientDropdownOpen = !this.clientDropdownOpen;
    if (this.clientDropdownOpen) {
      this.clientSearchTerm = '';
      this.projectDropdownOpen = false;
      setTimeout(() => this.clientSearchInput?.nativeElement.focus(), 0);
    }
  }

  selectClient(name: string) {
    this.form.masterClientName = name;
    this.clientDropdownOpen = false;
    this.clientSearchTerm = '';
    this.onMasterClientNameChange();
  }

  toggleProjectDropdown() {
    this.projectDropdownOpen = !this.projectDropdownOpen;
    if (this.projectDropdownOpen) {
      this.projectSearchTerm = '';
      this.clientDropdownOpen = false;
      setTimeout(() => this.projectSearchInput?.nativeElement.focus(), 0);
    }
  }

  selectProject(name: string) {
    this.form.dtProjectName = name;
    this.projectDropdownOpen = false;
    this.projectSearchTerm = '';
    this.onDtProjectNameChange();
  }

  onMasterClientNameChange() {
    this.dtProjectNames = Array.from(new Set(
      this.projectDetails
        .filter(p => p.ClientName === this.form.masterClientName)
        .map(p => p.RepLetterProjectNm)
    ));
    this.form.dtProjectName = '';
    this.contractNumbers = [];
    this.form.contractNumber = '';
    this.form.market = '';
    this.form.marketUnit = '';
    this.updateReviewName();
  }

  onDtProjectNameChange() {
    this.contractNumbers = this.projectDetails
      .filter(p => p.RepLetterProjectNm === this.form.dtProjectName)
      .map(p => p.ContractNumber);
    this.form.contractNumber = '';
    this.form.market = '';
    this.form.marketUnit = '';
    this.updateReviewName();
  }

  onContractNumberChange() {
    const match = this.projectDetails.find(
      p => p.RepLetterProjectNm === this.form.dtProjectName && p.ContractNumber === this.form.contractNumber
    );
    this.form.market = match?.Market ?? '';
    this.form.marketUnit = match?.MarketUnit ?? '';
  }

  onProposedYearChange(): void {
    this.updateReviewName();
  }

  getMonthsForQuarter(quarter: string): readonly MonthOption[] {
    return QUARTER_MONTH_MAP[quarter as Quarter] ?? [];
  }

  onQuarterChange(card: ReviewLocationCard): void {
    const allowedMonths = this.getMonthsForQuarter(card.quarter);
    if (!allowedMonths.includes(card.proposedMonth as MonthOption)) {
      card.proposedMonth = '';
    }
  }

  showSavedMonthOption(card: ReviewLocationCard): boolean {
    return !!card.proposedMonth &&
      !this.getMonthsForQuarter(card.quarter).includes(card.proposedMonth as MonthOption);
  }

 

  private buildLocationCards(): void {
    const prevCards = this.locationCards;
    this.locationCards = this.form.locationsToReview.map((loc) => {
      const existing = prevCards.find(card => card.location === loc);
      if (existing) {
        return { ...existing };
      }
      const countryForCity = this.getCountryForCity(loc);
      return {
        location: loc,
        deliveryCountry: countryForCity,
        quarter: '',
        proposedMonth: '',
        rcManager: '',
        rcSupervisor: '',
        rcReviewer: '',
        secondaryRcManager: '',
        secondaryRcSupervisor: '',
        secondaryRcReviewer: '',
        isParentLocation: false,
        reviewId: null,
        reviewTypeId: 1,
      };
    });

   
    if (this.locationCards.length > 0 && !this.locationCards.some(c => c.isParentLocation)) {
      this.locationCards[0].isParentLocation = true;
    }
    this.updateReviewName();
  }

  updateReviewName(): void {
    this.form.reviewName = this.buildReviewName();
    setTimeout(() => this.adjustReviewNameHeight(), 0);
  }

  get proposedYearDisplayName(): string {
    const match = this.proposedYears.find(y => y.ProposedYear.toString() === this.form.proposedYear);
    return match?.DisplayName ?? (this.getFinancialYearLabel(this.form.proposedYear) || this.form.proposedYear);
  }

  private getFinancialYearLabel(year: string): string {
    const trimmed = (year ?? '').trim();
    if (trimmed.length < 2) return '';
    return `FY${trimmed.slice(-2)}`;
  }

  private buildReviewName(): string {
    const parentLoc = this.locationCards.find(c => c.isParentLocation)?.location
      || this.locationCards[0]?.location
      || '';
    const parts = [
      this.getFinancialYearLabel(this.form.proposedYear),
      this.reviewTypeCode,
      this.form.masterClientName?.trim(),
      this.form.dtProjectName?.trim(),
      parentLoc,
    ].filter((part): part is string => !!part && part.length > 0);
    return parts.join('-');
  }

  private buildReviewNameForLocation(city: string): string {
    const parts = [
      this.getFinancialYearLabel(this.form.proposedYear),
      this.reviewTypeCode,
      this.form.masterClientName?.trim(),
      this.form.dtProjectName?.trim(),
      city,
    ].filter((part): part is string => !!part && part.length > 0);
    return parts.join('-');
  }

  saveAsDraft(): void {
    this.saveAndSubmitPayload('Draft', true);
  }

  submitForm(): void {
    this.submitAttempted = true;
    if (!this.usesGoLiveLayout && this.form.deliveryCountries.length === 0 && !this.locationCards.some(c => !!c.deliveryCountry)) {
      this.locationRequiredError = true;
      setTimeout(() => {
        this.locationField?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return;
    }
    this.locationRequiredError = false;
    if (this.validateForSubmit().length > 0) {
      return;
    }
    if (this.refreshDuplicateLocationErrors() || this.hasLocationPairErrors()) {
      setTimeout(() => {
        document.querySelector('.field-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return;
    }
    this.saveAndSubmitPayload(this.summary.statusOfReview || 'Planned', false);
  }

  private validateForSubmit(): string[] {
    const errors: string[] = [];

    if (!this.isValidAdditionalContractNumber(this.form.additionalContractNumber)) {
      this.additionalContractNumberError = this.getAdditionalContractNumberError(this.form.additionalContractNumber);
      errors.push(this.additionalContractNumberError);
    }

    if (!this.form.proposedYear) errors.push('Proposed Year is required.');
    if (!this.form.masterClientName) errors.push('Master Client Name is required.');
    if (!this.form.dtProjectName) errors.push('D&T Project Name is required.');
    if (!this.form.contractNumber) errors.push('Contract Number is required.');
    if (!this.form.market) errors.push('Market is required.');

    if (this.usesGoLiveLayout) {
      if (!this.form.marketUnit) errors.push('Market Unit is required.');
      if (this.isSpecialProject && !this.form.typeOfProject?.trim()) errors.push("Select the 'Type of Project'");

      this.locationCards.forEach((card, index) => {
        const label = this.locationCards.length > 1 ? `Location ${index + 1}` : 'Location';
        if (!card.deliveryCountry) errors.push(`${label}: Delivery Country is required.`);
        if (!card.location) errors.push(`${label}: Location to be reviewed is required.`);
        if (!card.rcManager) errors.push(`${label}: R&C Manager is required.`);
        if (!card.rcSupervisor) errors.push(`${label}: R&C Supervisor is required.`);
        if (!card.rcReviewer) errors.push(`${label}: R&C Reviewer is required.`);
      });
    } else {
      const hasDeliveryCountry = this.form.deliveryCountries.length > 0 || this.locationCards.some(c => !!c.deliveryCountry);
      const hasLocationToReview = this.form.locationsToReview.length > 0 || this.locationCards.some(c => !!c.location);
      if (!hasDeliveryCountry) errors.push('Delivery Country is required.');
      if (!hasLocationToReview) errors.push('Location to be reviewed is required.');
      this.locationCards.forEach((card, index) => {
        const label = this.locationCards.length > 1 ? `Location ${index + 1}` : 'Location';
        if (!card.deliveryCountry) errors.push(`${label}: Delivery Country is required.`);
        if (!card.location) errors.push(`${label}: Location to be reviewed is required.`);
        if (!card.rcManager) errors.push(`${label}: R&C Manager is required.`);
        if (!card.rcSupervisor) errors.push(`${label}: R&C Supervisor is required.`);
        if (!card.rcReviewer) errors.push(`${label}: R&C Reviewer is required.`);
      });
    }

    return errors;
  }

  private async saveAndSubmitPayload(currentStatus: string, asDraft = false): Promise<void> {
    this.isLoading = true;
    const createdBy = this.rebarAuth.getUserName() ?? '';
    const folder = this.form.reviewName.trim();

    const documents: ReviewDocument[] = await Promise.all(
      this.attachedFiles.map(async (file, index) => {
        const ext = file.name.includes('.') ? (file.name.split('.').pop() ?? '') : '';
        const fileContent = await this.readFileAsBase64(file);
        return {
          DocSeq: index + 1,
          DocumentType: 'Standard Review Attachment',
          FileName: file.name,
          StorageFilePath: folder ? `${folder}/${file.name}` : file.name,
          FileSize: file.size,
          FileType: ext,
          Description: '',
          fileContent,
          UploadedBy: createdBy,
          // ReviewId: this.reviewId ,
        };
      })
    );

    // Save As Draft keeps status as 'Draft'; Submit promotes Draft/Planned to 'Planned'.
    const normalizedStatus = (currentStatus || '').toLowerCase();
    const reviewStatus = asDraft
      ? 'Draft'
      : (normalizedStatus === 'draft' || normalizedStatus === 'planned' ? 'Planned' : currentStatus);
    const statusChanging = !asDraft && normalizedStatus !== 'planned';
    const payload = this.buildDynamicUpdatePayload(reviewStatus, createdBy, documents, statusChanging);

    this.reviewerService.updateReviewDynamic(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const errorMessages = this.filterAttachmentErrors(this.extractErrorMessages(response));
          if (errorMessages.length > 0) {
            this.isLoading = false;
            this.errorPopupMessages = errorMessages;
            this.showErrorPopup = true;
            return;
          }

          const savedRows = this.extractSavedReviewRows(response);
          if (asDraft) {
            this.handleDraftSuccess(savedRows);
            return;
          }
          this.handleSubmitSuccess(savedRows);
        },
        error: (err) => {
          console.error('[Save] Error:', err);
          this.isLoading = false;

          const errorMessage = this.extractHttpErrorMessage(err);
          const messages = this.filterAttachmentErrors(errorMessage ? [errorMessage] : []);
          if (messages.length > 0) {
            this.errorPopupMessages = messages;
            this.showErrorPopup = true;
          }
        }
      });
  }

  private buildDynamicUpdatePayload(
    reviewStatus: string,
    modifiedBy: string,
    documents: ReviewDocument[],
    statusChanging = true
  ): object {
    const orig = this._originalForm ?? this.form;

    // Generated by GitHub Copilot - Oct-22-2026
    // Field names must match those written on create (ProposedYearId/ClientName/ProjectName);
    // the Review Management grid reads these, so any other name creates an orphan field.
    const sharedFieldMap: Array<{ key: keyof ReviewBasicDetailsForm; dbField: string; dataType: string; toValue: (v: string) => string }> = [
      { key: 'proposedYear',     dbField: 'ProposedYearId', dataType: 'int',      toValue: v => this.getProposedYearId(v) },
      { key: 'masterClientName', dbField: 'ClientName',     dataType: 'nvarchar', toValue: v => v },
      { key: 'dtProjectName',    dbField: 'ProjectName',    dataType: 'nvarchar', toValue: v => v },
    ];
    const changedShared = sharedFieldMap.filter(({ key }) =>
      String((orig as any)[key] ?? '') !== String((this.form as any)[key] ?? ''));

    const commonFieldMap: Array<{ key: keyof ReviewBasicDetailsForm; dbField: string; dataType: string }> = [
      { key: 'contractNumber',           dbField: 'ContractNumber',           dataType: 'string' },
      { key: 'additionalContractNumber', dbField: 'AdditionalContractNumber', dataType: 'string' },
      { key: 'market',                   dbField: 'Market',                   dataType: 'string' },
      { key: 'marketUnit',               dbField: 'MarketUnit',               dataType: 'string' },
      { key: 'customerRelationshipName', dbField: 'CustomerRelationshipName', dataType: 'string' },
      { key: 'planName',                 dbField: 'PlanName',                 dataType: 'string' },
      { key: 'qadRecommendation',        dbField: 'QADRecommendation',        dataType: 'string' },
      { key: 'clientQuality',            dbField: 'ClientQualityCQI',         dataType: 'string' },
      { key: 'clientAccountLead',        dbField: 'ClientAccountLead',        dataType: 'string' },
      { key: 'operationAccountLead',     dbField: 'OperationAccountLead',     dataType: 'string' },
      { key: 'masterClientId',           dbField: 'MasterClientID',           dataType: 'string' },
      { key: 'comments',                 dbField: 'Comments',                 dataType: 'string' },
      { key: 'typeOfReview',             dbField: 'TypeOfReview',             dataType: 'string' },
      { key: 'typeOfProject',            dbField: 'TypeOfProject',            dataType: 'nvarchar' },
    ];

    const changedFields: { FieldName: string; FieldValue: string | null; DataType: string }[] = [];

    for (const { key, dbField, dataType, toValue } of changedShared) {
      changedFields.push({ FieldName: dbField, FieldValue: toValue(String((this.form as any)[key] ?? '')) || null, DataType: dataType });
    }

    for (const { key, dbField, dataType } of commonFieldMap) {
      const origVal = String((orig as any)[key] ?? '');
      const curVal  = String((this.form as any)[key] ?? '');
      if (origVal !== curVal) {
        changedFields.push({ FieldName: dbField, FieldValue: curVal || null, DataType: dataType });
      }
    }

    if (JSON.stringify(orig.deliveryCountries) !== JSON.stringify(this.form.deliveryCountries)) {
      changedFields.push({
        FieldName: 'DeliveryCountry',
        FieldValue: this.form.deliveryCountries.join(',') || null,
        DataType: 'string',
      });
    }

    // Keep SingleMultipleLocation in sync with the current number of location cards.
    const computedSingleMultiple = this.locationCards.length > 1 ? 'Multiple Location' : 'Single Location';
    if ((this.summary.singleMultipleLocation || '') !== computedSingleMultiple) {
      changedFields.push({ FieldName: 'SingleMultipleLocation', FieldValue: computedSingleMultiple, DataType: 'string' });
    }

    const locationFieldMap: Array<{ key: keyof ReviewLocationCard; dbField: string; dataType: string }> = [
      { key: 'deliveryCountry',       dbField: 'DeliveryCountry',    dataType: 'string' },
      { key: 'location',              dbField: 'LocationToBeReviewed', dataType: 'string' },
      { key: 'quarter',               dbField: 'Quarter',             dataType: 'string' },
      { key: 'proposedMonth',         dbField: 'ProposedMonth',       dataType: 'string' },
      { key: 'rcManager',             dbField: 'RCManager',           dataType: 'string' },
      { key: 'rcSupervisor',          dbField: 'RCSupervisor',        dataType: 'string' },
      { key: 'rcReviewer',            dbField: 'RCReviewer',          dataType: 'string' },
      { key: 'secondaryRcManager',    dbField: 'SecondaryRCManager',    dataType: 'string' },
      { key: 'secondaryRcSupervisor', dbField: 'SecondaryRCSupervisor', dataType: 'string' },
      { key: 'secondaryRcReviewer',   dbField: 'SecondaryRCReviewer',   dataType: 'string' },
      { key: 'isParentLocation',      dbField: 'IsParentLocation',    dataType: 'bit' },
    ];

    const locationFields: object[] = [];

    this.locationCards.forEach(card => {
      const origCard = card.reviewId != null
        ? this._originalLocationCards.find(c => c.reviewId === card.reviewId)
        : this._originalLocationCards.find(c => c.location === card.location);
      const cardFields: object[] = [];
      for (const { key, dbField, dataType } of locationFieldMap) {
        const origVal = origCard ? String((origCard as any)[key] ?? '') : null;
        const curVal  = String((card as any)[key] ?? '');
        if (origVal === null || origVal !== curVal) {
          cardFields.push({
            LocationName: card.location,
            ReviewID: card.reviewId ?? null,
            IsParentLocation: card.isParentLocation,
            FieldName: dbField,
            // Send '' (not null) for cleared fields: a null FieldValue row marks a removed
            // location, so a cleared Secondary R&C role must not look like a location delete.
            FieldValue: curVal,
            oldvalue: origVal || null,
            DataType: dataType,
          });
        }
      }
      // Shared fields live on every location's review row, so push changes (and the
      // per-location ReviewName) to each existing review, not only the top-level ReviewID.
      if (card.reviewId != null && changedShared.length > 0) {
        const pushField = (dbField: string, value: string, oldValue: string, dataType: string) => cardFields.push({
          LocationName: card.location,
          ReviewID: card.reviewId,
          IsParentLocation: card.isParentLocation,
          FieldName: dbField,
          FieldValue: value || null,
          oldvalue: oldValue || null,
          DataType: dataType,
        });
        changedShared.forEach(({ key, dbField, dataType, toValue }) => pushField(
          dbField,
          toValue(String((this.form as any)[key] ?? '')),
          toValue(String((orig as any)[key] ?? '')),
          dataType));
        pushField('ReviewName', this.buildReviewNameForLocation(card.location), '', 'string');
      }
      if (cardFields.length === 0 && this.locationCards.length > 1 && card.reviewId != null) {
        cardFields.push({
          LocationName: card.location,
          ReviewID: card.reviewId,
          IsParentLocation: card.isParentLocation,
          FieldName: 'IsParentLocation',
          FieldValue: card.isParentLocation ? 'true' : 'false',
          oldvalue: card.isParentLocation ? 'true' : 'false',
          DataType: 'bit',
        });
      }
      locationFields.push(...cardFields);
    });

    this._originalLocationCards
      .filter(orig => orig.reviewId != null && !this.locationCards.find(c => c.reviewId === orig.reviewId))
      .forEach(deleted => {
        locationFields.push({
          LocationName: deleted.location,
          ReviewID: deleted.reviewId,
          IsParentLocation: deleted.isParentLocation,
          FieldName: deleted.location,
          FieldValue: null,
          oldvalue: deleted.location,
          DataType: 'nvarchar',
        });
      });

    return {
      ReviewID: this.reviewId,
      ReviewName: this.form.reviewName || null,
      ...(statusChanging ? { ReviewStatus: reviewStatus } : {}),
      ModifiedBy: modifiedBy,
      Fields: changedFields,
      LocationFields: locationFields,
      Documents: documents,
    };
  }

  private handleSubmitSuccess(savedRows: any[] = []): void {
    const message = 'Data uploaded and saved successfully';
    this.successToastMessage = message;
    setTimeout(() => (this.successToastMessage = null), 5000);
    this.reloadWithFreshData(message, 'Planned', savedRows, true);
  }

  private handleDraftSuccess(savedRows: any[] = []): void {
    const message = 'Draft saved successfully';
    this.draftSavedMessage = true;
    setTimeout(() => (this.draftSavedMessage = false), 3000);
    this.reloadWithFreshData(message, 'Draft', savedRows);
  }

  private reloadWithFreshData(_message: string, _savedStatus: string, savedRows: any[] = [], collapseSchedule = false): void {
    const parentCard = this.locationCards.find(c => c.isParentLocation);
    const savedParentReviewId = this.getSavedReviewIdForLocation(savedRows, parentCard?.location ?? '');
    const reviewId = parentCard?.reviewId ?? savedParentReviewId ?? this.reviewId ?? null;

    if (reviewId == null) {
      return;
    }

    this.reviewerService.getReviewByIDDynamic(reviewId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (details: any) => {
          this.applyReviewDetails(details, false);
          if (collapseSchedule) {
            this.scheduleExpanded = false;
          }
          this.submitAttempted = false;
          this.attachedFiles = [];
          if (this.reviewId) {
            this.fetchGoLiveDynamicData(this.reviewId); 
          } else {
            this.isLoading = false;
          }
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  private buildCurrentDocumentsJson(): string {
    const folder = (this.form.reviewName ?? '').trim();
    const existing = this.prePopulatedDocuments.map(doc => ({
      FileName: doc.fileName,
      StorageFilePath: doc.filePath,
    }));
    const attached = this.attachedFiles
      .filter(f => !this.prePopulatedDocuments.some(d => d.fileName === f.name))
      .map(f => ({
        FileName: f.name,
        StorageFilePath: folder ? `${folder}/${f.name}` : f.name,
      }));
    return JSON.stringify([...existing, ...attached]);
  }

  private buildReviewDetailsFromCurrentState(currentStatus: string, savedRows: any[] = []): any[] {
    const state = history.state ?? {};
    const oldDetails: any[] = Array.isArray(state['reviewDetails']) ? state['reviewDetails'] : [];

    if (this.locationCards.length === 0) {
      const keptReviewId = this.reviewId
        ?? this._originalLocationCards.find(c => c.isParentLocation)?.reviewId
        ?? this._originalLocationCards[0]?.reviewId
        ?? null;
      const base = oldDetails.find(d => d.ReviewID === keptReviewId) ?? oldDetails[0] ?? {};
      return [{
        ReviewID: keptReviewId,
        ReviewName: this.form.reviewName,
        ReviewTypeCode: this.reviewTypeCode,
        ReviewTypeID: base.ReviewTypeID ?? 1,
        TypeOfReview: this.form.typeOfReview ?? '',
        TypeOfProject: this.form.typeOfProject ?? '',
        ProposedYear: this.form.proposedYear,
        MasterClientName: this.form.masterClientName,
        'D&TProjectName': this.form.dtProjectName,
        ContractNumber: this.form.contractNumber,
        AdditionalContractNumber: this.form.additionalContractNumber,
        Market: this.form.market,
        MarketUnit: this.form.marketUnit,
        CustomerRelationshipName: this.form.customerRelationshipName,
        PlanName: this.form.planName,
        QADRecommendation: this.form.qadRecommendation,
        ClientQualityCQI: this.form.clientQuality,
        ClientAccountLead: this.form.clientAccountLead,
        OperationAccountLead: this.form.operationAccountLead,
        MasterClientID: this.form.masterClientId,
        DeliveryCountry: this.form.deliveryCountries[0] ?? '',
        LocationCity: null,
        Quarter: null,
        ProposedMonth: null,
        RCManager: null,
        RCSupervisor: null,
        RCReviewer: null,
        SecondaryManager: null,
        SecondarySupervisor: null,
        SecondaryReviewer: null,
        IsParentLocation: true,
        CurrentStatus: currentStatus,
        SingleOrMultipleLocationReview: 'Single',
        Comments: this.form.comments ?? '',
        Documents: this.buildCurrentDocumentsJson(),
        ScheduledFieldworkStartDate: base.ScheduledFieldworkStartDate ?? this.summary.planFieldworkStartDate ?? '',
        ScheduledFieldworkEndDate: base.ScheduledFieldworkEndDate ?? this.summary.planFieldworkEndDate ?? '',
        FieldWorkStartDate: base.FieldWorkStartDate ?? '',
        FieldWorkEndDate: base.FieldWorkEndDate ?? '',
        TargetPublicationDate: base.TargetPublicationDate ?? this.summary.targetPublicationDate ?? '',
        ServiceLine: base.ServiceLine ?? this.summary.serviceLine ?? '',
        ParentChildType: base.ParentChildType ?? this.summary.parentChildType ?? '',
        ParentReviewIDTBD: base.ParentReviewIDTBD ?? (this.summary.parentReviewId ? Number(this.summary.parentReviewId) : null),
      }];
    }

    return this.locationCards.map(card => {
      const savedReviewId = this.getSavedReviewIdForLocation(savedRows, card.location);
      const resolvedReviewId = card.reviewId ?? savedReviewId ?? (card.isParentLocation ? this.reviewId : null) ?? null;
      const prev = oldDetails.find((old: any) =>
        resolvedReviewId != null
          ? old.ReviewID === resolvedReviewId
          : (old.LocationCity ?? '').trim().toLowerCase() === card.location.trim().toLowerCase()
      );

      return {
        ReviewID: resolvedReviewId,
        ReviewName: this.buildReviewNameForLocation(card.location),
        ReviewTypeCode: this.reviewTypeCode,
        ReviewTypeID: card.reviewTypeId ?? 1,
        TypeOfReview: this.form.typeOfReview ?? '',
        TypeOfProject: this.form.typeOfProject ?? '',
        ProposedYear: this.form.proposedYear,
        MasterClientName: this.form.masterClientName,
        'D&TProjectName': this.form.dtProjectName,
        ContractNumber: this.form.contractNumber,
        AdditionalContractNumber: this.form.additionalContractNumber,
        Market: this.form.market,
        MarketUnit: this.form.marketUnit,
        CustomerRelationshipName: this.form.customerRelationshipName,
        PlanName: this.form.planName,
        QADRecommendation: this.form.qadRecommendation,
        ClientQualityCQI: this.form.clientQuality,
        ClientAccountLead: this.form.clientAccountLead,
        OperationAccountLead: this.form.operationAccountLead,
        MasterClientID: this.form.masterClientId,
        DeliveryCountry: card.deliveryCountry || (this.form.deliveryCountries[0] ?? ''),
        LocationCity: card.location,
        Quarter: card.quarter,
        ProposedMonth: card.proposedMonth,
        RCManager: card.rcManager,
        RCSupervisor: card.rcSupervisor,
        RCReviewer: card.rcReviewer,
        SecondaryManager: card.secondaryRcManager,
        SecondarySupervisor: card.secondaryRcSupervisor,
        SecondaryReviewer: card.secondaryRcReviewer,
        IsParentLocation: card.isParentLocation,
        CurrentStatus: currentStatus,
        SingleOrMultipleLocationReview: this.locationCards.length > 1 ? 'Multiple' : 'Single',
        Comments: this.form.comments ?? '',
        Documents: card.isParentLocation ? this.buildCurrentDocumentsJson() : (prev?.Documents ?? '[]'),
        ScheduledFieldworkStartDate: prev?.ScheduledFieldworkStartDate ?? this.summary.planFieldworkStartDate ?? '',
        ScheduledFieldworkEndDate: prev?.ScheduledFieldworkEndDate ?? this.summary.planFieldworkEndDate ?? '',
        FieldWorkStartDate: prev?.FieldWorkStartDate ?? '',
        FieldWorkEndDate: prev?.FieldWorkEndDate ?? '',
        TargetPublicationDate: prev?.TargetPublicationDate ?? this.summary.targetPublicationDate ?? '',
        ServiceLine: prev?.ServiceLine ?? this.summary.serviceLine ?? '',
        ParentChildType: prev?.ParentChildType ?? this.summary.parentChildType ?? '',
        ParentReviewIDTBD: prev?.ParentReviewIDTBD
          ?? (this.summary.parentReviewId ? Number(this.summary.parentReviewId) : null),
      };
    });
  }

  private extractSavedReviewRows(response: any): any[] {
    const rows = Array.isArray(response)
      ? response
      : [response?.value ?? response?.recordset ?? response?.reviewDetails ?? response?.details ?? response];

    return rows
      .reduce((acc: any[], item: any) => acc.concat(Array.isArray(item) ? item : [item]), [])
      .filter((item: any) => item && typeof item === 'object' && item.ReviewID != null);
  }

  private getSavedReviewIdForLocation(rows: any[], location: string): number | null {
    const normalizedLocation = (location ?? '').trim().toLowerCase();
    if (!normalizedLocation || !Array.isArray(rows) || rows.length === 0) return null;

    const match = rows.find((row: any) => (row?.LocationCity ?? '').trim().toLowerCase() === normalizedLocation);
    return match?.ReviewID != null ? Number(match.ReviewID) : null;
  }

  private mergeServerWithInMemoryDetails(serverDetails: any[], inMemoryDetails: any[]): any[] {
    const hasDocs = (docs: any): boolean => {
      if (!docs) return false;
      try {
        const parsed = typeof docs === 'string' ? JSON.parse(docs) : docs;
        return Array.isArray(parsed) && parsed.length > 0;
      } catch {
        return false;
      }
    };
    const enrichedServer = serverDetails.map((srv: any) => {
      if (hasDocs(srv.Documents)) return srv;
      const mem = inMemoryDetails.find((m: any) =>
        (m.ReviewID != null && m.ReviewID === srv.ReviewID) ||
        (m.LocationCity ?? '').trim().toLowerCase() === (srv.LocationCity ?? '').trim().toLowerCase()
      );
      return mem && hasDocs(mem.Documents) ? { ...srv, Documents: mem.Documents } : srv;
    });

    const serverCities = new Set(
      enrichedServer.map((d: any) => (d.LocationCity ?? '').trim().toLowerCase())
    );
    const missing = inMemoryDetails.filter(
      d => !serverCities.has((d.LocationCity ?? '').trim().toLowerCase())
    );
    return [...enrichedServer, ...missing];
  }

  private stashReloadState(reviewId: number, details: any, message: string, parentLocation = ''): void {
    if (typeof history === 'undefined') return;
    const state = history.state ?? {};
    let reviewDetails = details ?? state['reviewDetails'];

    if (Array.isArray(reviewDetails)) {
      reviewDetails = this.normalizeReviewDetails(reviewDetails, parentLocation);
    }

    history.replaceState(
      { ...state, reviewId, reviewDetails, submitSuccessMessage: message },
      ''
    );
  }

 
  private normalizeReviewDetails(reviewDetails: any[], parentLocation = ''): any[] {
    const state = (typeof history !== 'undefined' ? history.state : null) ?? {};
    const oldDetails: any[] = Array.isArray(state['reviewDetails']) ? state['reviewDetails'] : [];

    let result = reviewDetails;
    if (oldDetails.length > 0) {
      result = result.map((freshItem: any) => {
        if (freshItem.Documents) return freshItem;
        const match = oldDetails.find((old: any) => old.ReviewID === freshItem.ReviewID);
        return { ...freshItem, Documents: match?.Documents ?? '[]' };
      });
    }

    const norm = (v: any) => (v ?? '').toString().trim().toLowerCase();
    const target = norm(parentLocation);
    if (target) {
      result = result.map((it: any) => ({
        ...it,
        IsParentLocation: norm(it?.LocationCity) === target,
      }));
    }

    return [...result].sort(
      (a, b) => (b?.IsParentLocation ? 1 : 0) - (a?.IsParentLocation ? 1 : 0)
    );
  }


  private extractHttpErrorMessage(err: any): string {
    const body = err?.error ?? err;
    const message = typeof body === 'string' ? body : body?.message;
    return typeof message === 'string' && message.trim().length > 0 ? message.trim() : '';
  }
  private extractErrorMessages(response: any): string[] {
    if (!Array.isArray(response)) return [];
    return response
      .map(item => item?.ErrorMessage)
      .filter((msg): msg is string => typeof msg === 'string' && msg.trim().length > 0);
  }

  private filterAttachmentErrors(messages: string[]): string[] {
    const hasAttachment = this.prePopulatedDocuments.length > 0 || this.attachedFiles.length > 0;
    if (!hasAttachment) return messages;
    return messages.filter(msg => !this.isAttachmentError(msg));
  }

  private isAttachmentError(msg: string): boolean {
    const m = (msg || '').toLowerCase();
    return m.includes('attach') || m.includes('document') || m.includes('upload') ||
      (m.includes('file') && m.includes('requir'));
  }

  openCancelReviewModal(): void {
    this.cancelReviewComment = '';
    this.cancelReviewFile = null;
    this.cancelReviewFileError = '';
    this.cancelReviewCommentError = false;
    this.showCancelReviewModal = true;
    document.body.style.overflow = 'hidden';
  }

  closeCancelReviewModal(): void {
    this.showCancelReviewModal = false;
    document.body.style.overflow = '';
  }

  onCancelReviewFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    const ext = (file.name.split('.').pop() ?? '').toLowerCase();
    if (!this.allowedFileExtensions.includes(ext)) {
      this.cancelReviewFileError = `"${file.name}" has an unsupported file type.`;
      input.value = '';
      return;
    }
    if (file.size > this.maxFileSizeBytes) {
      this.cancelReviewFileError = `"${file.name}" exceeds the 20 MB size limit.`;
      input.value = '';
      return;
    }
    this.cancelReviewFileError = '';
    this.cancelReviewFile = file;
    input.value = '';
  }

  removeCancelReviewFile(): void {
    this.cancelReviewFile = null;
  }

  async sendCancelReviewRequest(): Promise<void> {
    if (!this.isCancelReviewFormDirty || this.isSendingCancelRequest) return;

    if (!this.cancelReviewComment.trim()) {
      this.cancelReviewCommentError = true;
      return;
    }

    this.isSendingCancelRequest = true;
    this.isLoading = true;
    const user = this.rebarAuth.getUserName() ?? '';

    let documentsJson: string | null = null;
    if (this.cancelReviewFile) {
      const file = this.cancelReviewFile;
      const ext = file.name.includes('.') ? (file.name.split('.').pop() ?? '') : '';
      const fileContent = await this.readFileAsBase64(file);
      documentsJson = JSON.stringify([{
        DocumentID: null,
        DocumentType: ext,
        UploadedBy: user,
        UploadedDate: new Date().toISOString(),
        IsActive: true,
        Description: '',
        FileName: file.name,
        StorageFilePath: `R-${this.reviewId}/${file.name}`,
        fileContent,
        FileSize: file.size,
        FileType: ext,
      }]);
    }

    const params = {
      category: 'InsertNewRequestTesting',
      RequestType: 'Review Cancellation',
      Status: 'New',
      ReviewID: this.reviewId,
      AssignedTo: this.summary.rcManager || null,
      CancellationReason: this.cancelReviewComment.trim(),
      DocumentsJSON: documentsJson,
      RequestedBy: user,
      CreatedBy: user,
      Title: null,
      SubTitle: null,
      EmailEnterpriseID: null,
      FirstName: null,
      LastName: null,
      RCFunctionDesignation: null,
      Country: null,
      WorkLocation: null,
      ExpirationOn: null,
      OperationRequestType: null,
      OperationServiceLine: null,
      ContractNumber: null,
      MasterCustomerNumber: null,
      Market: null,
      MarketUnit: null,
      NotApplicableReason: null,
      RevisedTargetPublicationDate: null,
      RevisedDateReason: null,
      AcknowledgementText: null,
      AcknowledgementAccepted: null,
      Comments: null,
    };

    this.masterService.insertNewRequest(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSendingCancelRequest = false;
          this.isLoading = false;
          this.showCancelReviewModal = false;
          document.body.style.overflow = '';
          this.successToastMessage = 'The Request for New Cancel Review has been sent successfully';
          setTimeout(() => (this.successToastMessage = null), 5000);
        },
        error: () => {
          this.isSendingCancelRequest = false;
          this.isLoading = false;
        },
      });
  }

  closeErrorPopup() {
    this.showErrorPopup = false;
    this.errorPopupMessages = [];
  }

  closeSuccessToast() {
    this.successToastMessage = null;
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const commaIndex = result.indexOf(',');
        resolve(commaIndex >= 0 ? result.substring(commaIndex + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  onReviewStatusChange(status: string) {
    this.summary.statusOfReview = status;
    const section = this.getRelevantViewSection((status || '').toLowerCase().replace(/\s+/g, ' ').trim());
    if (section === 'reviewCycle') {
      this.reviewCycleExpanded = true;
    } else if (section === 'issueManagement') {
      this.reviewCycleExpanded = false;
      this.issueManagementExpanded = true;
    }
  }

  onToastNotification(message: string) {
    this.successToastMessage = message;
    setTimeout(() => (this.successToastMessage = null), 5000);
  }

  // Issues and Action Plans
  issueStatuses: any;
  actionPlanStatuses: any;

  getApiResponse(): any {
    this.reviewerService.getIssue(this.reviewId).subscribe(res => {this.issueStatuses = res.map(item => item.IssueStatus);});
    this.reviewerService.getActionPlans(this.reviewId).subscribe(res => {this.actionPlanStatuses = res.map(item => item.ActionPlanStatus);});
  }

  masterClientIdError: string = '';

  onMasterClientIdKeyPress(event: KeyboardEvent): void {
    if (event.key.length === 1 && !/[0-9]/.test(event.key)) {
      event.preventDefault();
      this.masterClientIdError = 'Only numbers are allowed';
    }
  }

  /** Strips non-digit characters (typed or pasted) and shows a warning when any were removed. */
  onMasterClientIdInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '');
    this.masterClientIdError = digits !== input.value ? 'Only numbers are allowed' : '';
    input.value = digits;
    this.form.masterClientId = digits;
  }

  additionalContractNumberError: string = '';

  onAdditionalContractNumberKeyPress(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    if (event.key.length === 1 && !/[0-9]/.test(event.key)) {
      event.preventDefault();
      this.additionalContractNumberError = this.getAdditionalContractNumberError(input.value);
    } else if (input.value.length >= 10) {
      event.preventDefault();
    }
  }

  onAdditionalContractNumberInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const original = input.value;
    const numericOnly = original.replace(/\D/g, '').slice(0, 10);
    input.value = numericOnly;
    this.form.additionalContractNumber = numericOnly;
    this.additionalContractNumberError =
      /\D/.test(original) || !this.isValidAdditionalContractNumber(numericOnly)
        ? this.getAdditionalContractNumberError(numericOnly)
        : '';
  }

  // Already starts with 9 -> only the length is wrong; otherwise show the full rule.
  getAdditionalContractNumberError(value: string | null | undefined): string {
    return String(value ?? '').trim().startsWith('9')
      ? 'Additional Contract Number must be a 10-digit number'
      : 'Additional Contract Number must be a 10-digit number starting with 9';
  }

  // Optional field: empty is valid; otherwise exactly 10 digits starting with 9.
  isValidAdditionalContractNumber(value: string | null | undefined): boolean {
    const v = String(value ?? '').trim();
    return !v || /^9\d{9}$/.test(v);
  }

}